// src/Web/operacionesdia.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createApiClient } from '../lib/apiClient';
import { notifySuccess } from "../lib/notifications";
import { operationDeleteError } from "../lib/operationDeleteError";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiTrash2,
  FiX,
  FiSave,
  FiEdit2,
  FiSearch,
  FiImage,
  FiCalendar,
  FiChevronLeft,
  FiMenu,
  FiRefreshCw,
  FiTruck,
} from "react-icons/fi";
import { useUserId } from "../UserIdContext";
import {
  readDepositoFromSearch,
  resolveDepositoRoute,
} from "../lib/depositoRoute";
import "./usuarios.css";
import "./TruckCreationFields.css";
import "./DailyOperationsLayout.css";
import TruckLogoDropdown from "./TruckLogoDropdown";
import { filterTruckLogos as filtrarLogosPorMovimiento, getTruckLogoLabel, getTruckLogoImageFile, isTruckLogoAllowed } from "../lib/truckLogos";
import { getOperationDayState } from "../lib/operationDayState";
import { getOperationSiel, getOperationFacturacion as getOperacionFacturacion, withOperationControl } from "../lib/operationControls";
import OperationDapOrder from "./OperationDapOrder";
import FiscalOutChecks from "./FiscalOutChecks";
import { operationalToday } from "../lib/dayAccess";
import OperationTransferIndicator from "./OperationTransferIndicator";
import { getOperationTransfers } from "../lib/operationTransfers";
import ScheduledActivities, { ScheduledActivityMark } from "./ScheduledActivities";

/* ================== ESTILOS EXTRA ================== */

const EXTRA_STYLES = `
:root{
  --bg:#07131a;
  --panel:rgba(13,25,36,.6);
  --panel-strong:rgba(13,25,36,.85);
  --panel-solid:#0d1924;
  --stroke:#113143;
  --text:#e9f2ff;
  --muted:#9bb6d9;
  --primary:#0A9C5E;
  --primary-700:#0C7A4C;
  --accent:#65f9a1;
  --neon:#42ffb3;
  --neon-soft:rgba(66,255,179,.16);
  --neon-line:#42ffb3;
  --neon-glow:rgba(66,255,179,.26);
  --radius:18px;
  --shadow:0 20px 60px rgba(0,0,0,.35);
  --t:.25s cubic-bezier(.2,.7,.2,1);
}

.users-wrap{ color:var(--text); padding:8px 8px 40px; }
.users-header{ display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:16px; }
.title-stack h1{ margin:0; font-size:clamp(22px,2.2vw,32px); font-weight:800; color:#fff; }
.muted, .subtitle{ margin:0; color:var(--muted); }

.toolbar{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.input-search{
  display:flex; align-items:center; gap:8px;
  background:var(--panel-strong); border:1px solid var(--stroke);
  padding:10px 12px; border-radius:12px; min-width:220px;
}
.input-search.input-search--active{
  border-color:#1f6a50;
  box-shadow:0 0 0 3px rgba(10,156,94,.12);
}
.input-search.input-search--active input{
  border-color:#1f6a50;
  background:rgba(13,25,36,.92);
  box-shadow:0 0 0 3px rgba(10,156,94,.18);
}
.input-search input{ background:transparent; border:0; outline:0; color:var(--text); width:140px; cursor:pointer; }
.input-search svg{ color:var(--muted); }

.btn{
  display:inline-flex; align-items:center; gap:10px;
  border-radius:14px; padding:9px 16px; cursor:pointer;
  border:1px solid rgba(255,255,255,.08);
  background:
    radial-gradient(120% 160% at 50% 0%, rgba(255,255,255,.08), transparent 70%),
    linear-gradient(180deg, rgba(14,26,38,.95), rgba(6,11,18,.9));
  color:var(--text);
  font-weight:600;
  transition:transform .16s ease, box-shadow .16s ease, border-color .16s ease, background .16s ease;
  box-shadow:0 8px 28px rgba(0,0,0,.35);
  white-space:nowrap;
  position:relative;
  overflow:hidden;
}
.btn::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(130deg, rgba(255,255,255,.18), transparent 55%);
  opacity:0;
  transition:opacity .2s ease;
}
.btn:hover{
  transform:translateY(-1px);
  border-color:rgba(255,255,255,.25);
}
.btn:hover::after{ opacity:1; }
.btn.solid{
  background:linear-gradient(160deg, #0FB872, #0B8A56);
  border-color:rgba(255,255,255,.05);
  color:#04140e;
  box-shadow:0 12px 32px rgba(15,184,114,.35);
}
.btn.solid::after{ display:none; }
.btn.solid:hover{ transform:translateY(-2px); }
.btn.icon{
  width:40px; height:40px;
  display:inline-flex; justify-content:center; align-items:center;
  padding:0; border-radius:12px;
  font-size:18px;
}
.btn.icon svg{ font-size:20px; }
.btn.danger{
  background:linear-gradient(160deg, #9c0a2d, #7a0c25);
  color:#fff;
  border-color:transparent;
}
.btn.danger:hover{ box-shadow:0 10px 30px rgba(156,10,45,.35); }

.panel{ background:var(--panel); border:1px solid var(--stroke); border-radius:var(--radius); box-shadow:var(--shadow); padding:14px; }
.panel-diario{
  border-color:var(--neon-line);
  background:linear-gradient(180deg, rgba(7,24,20,.94), rgba(9,22,31,.94));
  box-shadow:none;
}
.empty{ text-align:center; color:var(--muted); padding:18px 8px; font-size:13px; }

/* DAILY GRID COMPACTO */
.daily-grid{
  display:grid;
  grid-template-columns: 1fr;
  gap:10px;
  margin-top:6px;
}

/* Layout tipo â€œplanillaâ€ como tu imagen */
@media (min-width:1100px){
  .daily-grid{
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-areas:
      "fi-in fi-eg fe-in fe-eg"
      "na-in na-eg obs-fi obs-na";
    align-items:stretch;
  }

  .area-fi-in{ grid-area: fi-in; }
  .area-fi-eg{ grid-area: fi-eg; }
  .area-fe-in{ grid-area: fe-in; }
  .area-fe-eg{ grid-area: fe-eg; }
  .area-obs-fi{ grid-area: obs-fi; }
  .area-na-in{ grid-area: na-in; }
  .area-na-eg{ grid-area: na-eg; }
  .area-obs-na{ grid-area: obs-na; }
}

@media (min-width:1600px){
  .daily-grid{
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-template-areas:
      "fi-in fi-eg fe-in fe-eg na-in na-eg"
      "obs-fi obs-fi obs-fi obs-na obs-na obs-na";
  }
}

.daily-card{
  position:relative;
  border-radius:18px;
  border:1px solid var(--neon-line);
  background:linear-gradient(180deg, rgba(8,24,22,.96), rgba(8,20,31,.96));
  box-shadow:none;
  padding:10px 12px;
  display:flex;
  flex-direction:column;
  gap:8px;
  min-height:60px;
}
.daily-card::before{
  content:"";
  position:absolute;
  inset:0;
  border-radius:inherit;
  pointer-events:none;
  box-shadow:none;
}
.daily-card-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
}
.daily-card-title{
  font-weight:800;
  font-size:15px;
  color:#f1fff8;
  letter-spacing:.01em;
  text-shadow:none;
}
.daily-card-sub{ font-size:11px; color:#b3f7d1; }

.daily-card-body{
  margin-top:4px;
  display:flex;
  flex-direction:column;
  flex-wrap:nowrap;
  gap:8px;
  align-items:flex-start;
  justify-content:flex-start;
  min-width:0;
}

/* BotÃ³n + en cada panel */
.cell-add{
  width:26px; height:26px; border-radius:10px;
  border:1px solid rgba(66,255,179,.55);
  background:rgba(10,33,27,.96);
  color:#edfff6;
  display:inline-flex; align-items:center; justify-content:center;
  cursor:pointer; transition:background .18s ease, border-color .18s ease;
  box-shadow:none;
}
.cell-add:hover{
  background:rgba(11,37,30,.98);
  border-color:rgba(66,255,179,.55);
  box-shadow:none;
}
.cell-add:active{ transform:none; }

/* ITEM OPERACIÃ“N */
.board-item{
  border:1px solid rgba(66,255,179,.22);
  border-radius:14px;
  padding:8px 10px;
  background:linear-gradient(180deg, rgba(11,28,25,.94), rgba(10,22,33,.96));
  color:#fff;
  display:flex;
  flex-direction:column;
  gap:4px;
  text-align:left;
  font-size:12px;
  transition:border-color .15s ease, background .15s ease;
  box-shadow:none;
  width:min(100%, 320px);
  max-width:100%;
  min-width:0;
  box-sizing:border-box;
  flex:0 0 auto;
}
.daily-card .board-item{ width:fit-content; align-self:flex-start; }
.board-item.pending{
  border-color:#f97316;
  background:linear-gradient(145deg, rgba(248,113,113,.25), rgba(15,23,42,.9));
}
.board-item:hover{
  border-color:rgba(66,255,179,.22);
  background:linear-gradient(180deg, rgba(11,28,25,.94), rgba(10,22,33,.96));
  box-shadow:none;
}
.board-item.actionable:focus-visible{
  outline:2px solid rgba(101,249,161,.75);
  outline-offset:2px;
}

/* Comentarios: estilo distinto */
.board-item.comentario{
  width:min(100%, 420px);
  max-width:100%;
  background:linear-gradient(145deg, rgba(148,163,184,.24), rgba(15,23,42,.96));
  border-style:dashed;
  border-color:rgba(148,163,184,.8);
}

.board-item-header{
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto;
  align-items:flex-start;
  gap:6px;
  min-width:0;
}
.daily-card .board-item-main{
  display:flex;
  align-items:center;
  gap:6px;
  min-width:0;
}
.daily-card .board-item-main > .board-item-title{ flex:0 1 auto; }
.daily-card .board-item-title{
  font-weight:700;
  font-size:11px;
  min-width:0;
  overflow-wrap:anywhere;
}
.board-item-open{
  width:20px;
  height:20px;
  border-radius:7px;
  border:1px solid rgba(66,255,179,.4);
  background:rgba(7,24,19,.86);
  color:#edfff6;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  transition:border-color .15s ease, background .15s ease;
  flex:0 0 auto;
}
.board-item-open:hover{
  border-color:rgba(66,255,179,.4);
  background:rgba(7,24,19,.86);
}
.board-item-open:active{
  transform:none;
}
.board-item-open:focus-visible{
  outline:2px solid rgba(101,249,161,.75);
  outline-offset:2px;
}
.board-item-open svg{
  font-size:10px;
}
.board-item-note{
  margin-top:8px;
  padding:7px 8px;
  border-radius:10px;
  border:1px solid rgba(148,163,184,.18);
  background:rgba(4,12,18,.34);
  min-width:0;
}
.board-item-note-label{
  display:block;
  margin-bottom:3px;
  color:var(--muted);
  font-size:10px;
  font-weight:800;
  letter-spacing:.04em;
  text-transform:uppercase;
}
.board-item-note-text{
  color:#e5eefc;
  font-size:11px;
  line-height:1.35;
  white-space:pre-wrap;
  overflow-wrap:anywhere;
}
.board-item-note-entry + .board-item-note-entry{
  margin-top:2px;
}
.board-item-note-truck{
  color:#fff;
  font-weight:900;
  text-decoration:underline;
  text-decoration-color:#fff;
  text-decoration-thickness:2px;
  text-underline-offset:2px;
}

.daily-card .empty{
  color:#bfffdc;
  text-align:left;
  padding:12px 8px 10px;
}

.obs-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:8px 10px;
  border-radius:12px;
  border:1px solid rgba(66,255,179,.24);
  background:linear-gradient(180deg, rgba(10,27,24,.9), rgba(9,19,29,.94));
  box-shadow:none;
  min-width:0;
}
.obs-row-main{
  display:flex;
  align-items:center;
  gap:10px;
  flex:1 1 auto;
  min-width:0;
  cursor:pointer;
}
.obs-row-main input{
  accent-color:#42ffb3;
}
.obs-row-text{
  font-size:13px;
  color:#effff7;
  flex:1 1 auto;
  min-width:0;
  overflow-wrap:anywhere;
}
.obs-row.done .obs-row-text{
  color:#94a3b8;
  text-decoration:none;
}
.obs-row-actions{
  display:flex;
  align-items:center;
  gap:6px;
  flex:0 0 auto;
  opacity:0;
  transform:translateY(-2px);
  pointer-events:none;
  transition:opacity .18s ease, transform .18s ease;
}
.obs-row:hover .obs-row-actions,
.obs-row:focus-within .obs-row-actions{
  opacity:1;
  transform:translateY(0);
  pointer-events:auto;
}
.obs-action{
  display:inline-flex;
  align-items:center;
  gap:5px;
  padding:5px 9px;
  border-radius:9px;
  border:1px solid rgba(66,255,179,.35);
  background:rgba(7,24,19,.9);
  color:#edfff6;
  cursor:pointer;
  font-size:11px;
  font-weight:700;
  transition:border-color .15s ease, background .15s ease;
}
.obs-action:hover{
  border-color:rgba(66,255,179,.6);
  background:rgba(9,31,24,.98);
}
.obs-action.danger{
  border-color:rgba(248,113,113,.5);
  color:#ffe1e1;
}
.obs-action.danger:hover{
  border-color:rgba(248,113,113,.78);
  background:rgba(66,16,23,.98);
}
@media (hover:none){
  .obs-row-actions{
    opacity:1;
    transform:none;
    pointer-events:auto;
  }
}

/* CAMIONES */
.truck-logos{
  display:grid;
  grid-template-columns: repeat(5, minmax(0,1fr));
  gap:3px;
  justify-items:center;
  align-items:center;
  padding-bottom:0;
}
.truck-logo{
  width:26px;
  height:26px;
  border-radius:999px;
  border:1px solid rgba(148,163,184,.6);
  background:rgba(15,23,42,.9);
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  overflow:hidden;
  transition:border-color .15s ease,
           transform .08s ease,
           box-shadow .15s ease,
           background .15s ease;
  position:relative;
}
.truck-logo img{
  width:100%;
  height:100%;
  object-fit:cover;
}
.truck-logo > svg{
  width:15px;
  height:15px;
  color:#e9f2ff;
}
.truck-logo span{
  font-size:11px;
}
.truck-logo:hover{
  border-color:#22c55e;
  box-shadow:0 0 0 1px rgba(34,197,94,.6);
  transform:translateY(-1px);
}
.truck-logo.pending{
  border-color:#f97316;
  box-shadow:0 0 0 1px rgba(248,113,113,.7);
}
.truck-logo.inprogress{
  border-color:#facc15;
  box-shadow:0 0 0 1px rgba(250,204,21,.7);
}
.truck-logo.finalizado{
  border-color:#22c55e;
  box-shadow:0 0 0 1px rgba(34,197,94,.7);
}
.truck-logo .mini-edit{
  position:absolute;
  top:3px;
  right:3px;
  width:16px;
  height:16px;
  border-radius:6px;
  border:1px solid rgba(255,255,255,.3);
  background:rgba(0,0,0,.4);
  color:#e9f2ff;
  display:grid;
  place-items:center;
  font-size:10px;
  opacity:0;
  transition:opacity .15s ease, transform .12s ease;
}
.truck-logo:hover .mini-edit{
  opacity:1;
  transform:translateY(-1px);
}

/* POPUP WIN98 */
.win98-pop{
  position:fixed;
  background:#f2f2f2;
  color:#0f172a;
  border:1px solid #6b7280;
  box-shadow:2px 2px 0 #000, 4px 4px 0 #6b7280;
  padding:10px 10px 8px;
  width:min(280px, 92vw);
  z-index:200;
  font-size:13px;
  border-radius:4px;
}
.win98-pop h4{
  margin:0 0 6px 0;
  font-size:13px;
  font-weight:800;
}
.win98-row{
  display:flex;
  align-items:center;
  gap:6px;
  margin-bottom:8px;
}
.win98-pop label{ font-weight:700; min-width:72px; }
.win98-pop select{
  flex:1;
  padding:6px 8px;
  border:1px solid #6b7280;
  border-radius:4px;
  background:#fff;
  color:#0f172a;
  font-size:12px;
  max-width:100%;
  box-sizing:border-box;
  min-width:0;
}
.win98-list{
  border:1px solid #9ca3af;
  background:#fff;
  padding:6px;
  border-radius:4px;
  max-height:160px;
  overflow:auto;
  margin-bottom:8px;
}
.win98-name{
  flex:1.2;
  font-weight:700;
  color:#0f172a;
}
.win98-subtitle{
  font-weight:700;
  margin:0 0 6px 0;
  color:#111827;
  font-size:12px;
}
.win98-empty{
  padding:6px 0 10px;
  color:#374151;
  font-size:12px;
}
.win98-actions{ display:flex; justify-content:flex-end; gap:8px; }
.win98-btn{
  padding:6px 10px;
  border:1px solid #6b7280;
  background:linear-gradient(180deg, #fff, #d1d5db);
  color:#0f172a;
  border-radius:4px;
  cursor:pointer;
  font-weight:700;
}

/* DRAWER */
.drawer-overlay{
  position:fixed;
  inset:0;
  background:radial-gradient(160% 120% at 50% 10%, rgba(101,249,161,.06), transparent 45%), rgba(0,0,0,.7);
  backdrop-filter:blur(4px);
  opacity:0;
  pointer-events:none;
  transition:opacity var(--t);
  z-index:60;
}
.drawer-overlay.show{ opacity:1; pointer-events:auto; }
.drawer{
  position:fixed;
  top:50%;
  left:50%;
  transform:translate(-50%, -46%) scale(.94);
  width:min(900px, 96vw);
  max-height:calc(100vh - 32px);
  background:linear-gradient(180deg, rgba(16,26,34,.96), rgba(8,14,22,.94));
  border:1px solid rgba(42,71,90,.7);
  border-radius:24px;
  box-shadow:0 36px 120px rgba(0,0,0,.62), 0 0 0 1px rgba(255,255,255,.02) inset;
  transition:transform var(--t), opacity var(--t);
  opacity:0;
  display:flex;
  flex-direction:column;
  pointer-events:none;
  overflow:hidden;
  z-index:70;
}
.drawer.ops-modal{
  width:min(860px, 95vw);
  max-height:calc(100vh - 24px);
}
.drawer.ops-modal--wide{ width:min(940px, 95vw); }
.drawer.ops-modal .drawer-header{
  gap:10px;
  padding:14px 16px 12px;
}
.drawer.ops-modal .drawer-header h3{
  font-size:18px;
}
.drawer.ops-modal .drawer-body{
  padding:14px 16px 12px;
}
.drawer.ops-modal .drawer-footer{
  gap:8px;
  padding:10px 16px;
}
.drawer.ops-modal .drawer-footer > div{
  gap:6px;
}
.drawer.ops-modal .panel{
  padding:12px;
  border-radius:16px;
}
.drawer.ops-modal .field{
  margin-bottom:8px;
}
.drawer.ops-modal .field input,
.drawer.ops-modal .field select{
  padding:8px 10px;
}
.drawer.ops-modal .field textarea{
  padding:8px 10px;
}
.drawer.ops-modal .btn{
  gap:8px;
  padding:8px 14px;
  border-radius:12px;
}
.drawer.ops-modal .btn.icon{
  width:38px;
  height:38px;
  border-radius:10px;
}
.drawer.open{
  transform:translate(-50%, -50%) scale(1);
  opacity:1;
  pointer-events:auto;
}
.drawer-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  padding:16px 20px 14px;
  border-bottom:1px solid rgba(17,49,67,.8);
  position:sticky;
  top:0;
  background:linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,0));
  z-index:2;
}
.drawer-header h3{
  margin:0;
  font-size:20px;
  letter-spacing:.2px;
}
.drawer-body{
  padding:16px 20px 14px;
  overflow:auto;
  background:radial-gradient(140% 140% at 4% 0%, rgba(101,249,161,.06), transparent 32%), var(--panel-solid);
}
.drawer-footer{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  flex-wrap:wrap;
  padding:12px 20px;
  border-top:1px solid rgba(17,49,67,.8);
  background:linear-gradient(180deg, rgba(10,16,24,.98), rgba(10,16,24,.92));
  position:sticky;
  bottom:0;
  z-index:2;
}
.drawer-footer > div{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}

/* FORM */
.field{ display:flex; flex-direction:column; gap:6px; margin-bottom:10px; }
.field label{ color:var(--muted); font-size:.9rem; font-weight:700; }
.field input, .field select{
  background:var(--panel-strong); color:var(--text);
  border:1px solid var(--stroke); border-radius:12px;
  padding:9px 11px; outline:none;
  transition:border-color var(--t), background var(--t), box-shadow var(--t);
}
.field input:focus, .field select:focus{ border-color:#1f6a50; background:rgba(13,25,36,.92); box-shadow:0 0 0 3px rgba(10,156,94,.18); }

.field textarea{
  background:var(--panel-strong);
  color:var(--text);
  border:1px solid var(--stroke);
  border-radius:12px;
  padding:10px 12px;
  outline:none;
  resize:vertical;
}
.field textarea:focus{
  border-color:#1f6a50;
  background:rgba(13,25,36,.92);
  box-shadow:0 0 0 3px rgba(10,156,94,.18);
}

/* Dropdown de clientes (autocomplete) */
.cliente-field{
  position:relative;
  overflow:visible;
  align-self:start;
}
.cliente-dropdown{
  position:absolute;
  top:calc(100% + 8px);
  left:0;
  right:0;
  z-index:130;
  max-height:260px;
  overflow:auto;
  padding:6px;
  border-radius:12px;
  border:1px solid #33cc99;
  background:linear-gradient(180deg, rgba(15,50,42,.98), rgba(9,34,29,.98));
  box-shadow:0 12px 28px rgba(0,0,0,.35), 0 0 0 1px rgba(51,204,153,.2);
}
.cliente-dropdown .empty{
  color:#dff7ee;
  padding:10px 8px;
}
.cliente-opt{
  width:100%;
  text-align:left;
  border-radius:10px;
  border:1px solid rgba(51,204,153,.35);
  background:rgba(10,42,35,.95);
  color:#f4fff9;
  padding:8px 10px;
  cursor:pointer;
  transition:border-color .16s ease, background .16s ease;
}
.cliente-opt + .cliente-opt{
  margin-top:6px;
}
.cliente-opt:hover{
  border-color:#66ffd1;
  background:rgba(17,70,58,.95);
}
.cliente-opt-main{
  display:flex;
  justify-content:space-between;
  gap:10px;
}
.cliente-opt-sub{
  color:#bfffe8;
  font-size:12px;
  margin-top:4px;
}
.cliente-opt-cuit{
  font-family:monospace;
  font-size:12px;
  color:#bfffe8;
  opacity:.95;
}

.form-grid{ display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-grid.full{ grid-template-columns:1fr; }
.form-grid.camion{ grid-template-columns: 1.4fr 1.4fr auto; }
.truck-obs-add{
  display:grid;
  grid-template-columns:minmax(280px, 1fr) auto;
  gap:12px;
  align-items:center;
}
.truck-obs-add .btn{
  min-width:132px;
  justify-content:center;
}
.truck-obs-state{
  width:150px;
  flex:0 0 150px;
  max-width:100%;
}
.row-line.truck-obs-row{
  padding:8px 10px;
  border-bottom:1px solid rgba(148,163,184,.18);
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto;
  gap:12px;
  align-items:center;
}
.row-line.truck-obs-row:last-child{
  border-bottom:none;
}
.truck-obs-main{
  min-width:0;
}
.truck-obs-main input{
  width:100%;
}
.truck-obs-main b{
  display:block;
  overflow-wrap:anywhere;
}
.truck-obs-controls{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-start;
}

/* LOGO SELECT */
.logo-select-field{ min-width:0; }
.logo-grid{
  display:grid;
  grid-template-columns:repeat(var(--logo-count, 5), minmax(0, 1fr));
  gap:8px;
  width:100%;
  max-width:calc(var(--logo-count, 5) * 56px + (var(--logo-count, 5) - 1) * 8px);
  align-items:center;
}
.logo-item{
  position:relative;
  display:grid;
  place-items:center;
  box-sizing:border-box;
  width:100%;
  min-width:0;
  aspect-ratio:1;
  border:1px solid rgba(255,255,255,.10);
  background:rgba(255,255,255,.03);
  border-radius:50%;
  padding:0;
  overflow:visible;
  cursor:pointer;
  transition:transform .12s ease, border-color .12s ease;
}
.logo-item:hover{
  transform:translateY(-1px);
  border-color:rgba(101,249,161,.55);
}
.logo-item.selected{
  border-color:#22c55e;
  box-shadow:0 0 0 3px #0d1924, 0 0 0 6px #35e68a;
}
.logo-item.selected::after{
  content:"✓";
  position:absolute;
  right:-3px;
  bottom:-3px;
  z-index:1;
  display:grid;
  place-items:center;
  box-sizing:border-box;
  width:20px;
  height:20px;
  border:2px solid #0d1924;
  border-radius:50%;
  background:#22c55e;
  color:#052e16;
  font-size:14px;
  font-weight:900;
  line-height:1;
  pointer-events:none;
}
.logo-item:focus-visible{
  outline:2px solid #65f9a1;
  outline-offset:2px;
}
.logo-item img{
  display:block;
  width:100%;
  height:100%;
  object-fit:cover;
  border-radius:50%;
}
.logo-empty{
  grid-column:1 / -1;
  border:1px dashed rgba(255,255,255,.20);
  border-radius:14px;
  padding:16px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:10px;
  color:var(--muted);
}
@media (prefers-reduced-motion: reduce){
  .btn, .board-item, .daily-card{ transition:none !important; }
}

/* === FIX FULL WIDTH / SIN FRANJAS === */
html, body, #root {
  width: 100%;
  max-width: 100%;
  margin: 0;
  overflow-x: hidden;
}

/* si tu layout usa un wrapper centrado tipo container */
.users-wrap,
.calendar-wrap,
.panel,
.calendar-board {
  width: 100%;
  max-width: none !important;
}

/* si en usuarios.css existe algo tipo .container / .page / .content */
.container,
.page,
.content,
.main-content,
.app-container {
  max-width: none !important;
  width: 100% !important;
}

/* asegura que el tablero no genere scroll horizontal */
.board-grid-wrap {
  overflow-x: hidden !important;
}

/* Botones centrales calendario */
.toolbar-center{
  display:flex;
  justify-content:center;
  align-items:center;
  gap:12px;
  width:100%;
}

.daily-card .truck-logos-header{
  display:grid;
  grid-template-columns:repeat(var(--truck-columns, 4), minmax(0, 1fr));
  align-items:center;
  justify-content:flex-start;
  gap:4px;
  flex:0 0 auto;
  min-width:0;
  width:calc(var(--truck-columns, 4) * 26px + (var(--truck-columns, 4) - 1) * 4px);
  max-width:60%;
}
.daily-card .truck-logos-header .truck-logo{ width:100%; height:auto; aspect-ratio:1; min-width:0; }

/* MENU HAMBURGUESA POR OPERACION */
.op-camiones-list{
  display:grid;
  gap:8px;
}

.op-camion-card{
  background:rgba(9,18,28,.52);
  border:1px solid rgba(17,49,67,.78);
  border-radius:16px;
  padding:10px 12px;
}

.op-camion-card.clickable{
  cursor:pointer;
  transition:border-color .16s ease, transform .12s ease, background .16s ease;
}

.op-camion-card.clickable:hover{
  border-color:rgba(101,249,161,.45);
  background:rgba(12,24,36,.75);
  transform:translateY(-1px);
}

.op-camion-card.clickable:focus-visible{
  outline:none;
  border-color:#22c55e;
  box-shadow:0 0 0 3px rgba(34,197,94,.18);
}

.op-camion-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:6px;
}

.op-camion-title{
  display:flex;
  align-items:center;
  gap:10px;
  font-weight:800;
  font-size:16px;
}

.op-camion-dot{
  width:10px;
  height:10px;
  border-radius:999px;
  background:#22c55e;
  box-shadow:0 0 0 4px rgba(34,197,94,.15);
  flex:0 0 auto;
}

.op-camion-open{
  font-size:12px;
  color:var(--muted);
  font-weight:700;
  white-space:nowrap;
}

.op-camion-row{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
  padding:5px 0;
  border-bottom:1px solid rgba(148,163,184,.18);
  font-size:12px;
}

.op-camion-row:last-child{
  border-bottom:none;
  padding-bottom:0;
}

.op-camion-row-name{
  min-width:0;
}

.op-camion-row-state{
  white-space:nowrap;
  opacity:.92;
}

.op-camion-empty{
  padding:2px 0 0;
  color:var(--muted);
  font-size:12px;
}

@media (max-width:700px){
  .daily-card{
    padding:9px 10px;
  }

  .daily-card-body{
    gap:6px;
  }

  .board-item{
    width:100%;
    max-width:none;
    padding:7px 8px;
  }

  .board-item-header{
    align-items:center;
    gap:4px;
  }

  .board-item-main{
    gap:6px;
  }

  .daily-card .board-item-title{
    font-size:11px;
    line-height:1.15;
  }

  .board-item-open{
    width:18px;
    height:18px;
    border-radius:6px;
  }

  .board-item-open svg{
    font-size:9px;
  }

  .daily-card .truck-logos-header{
    width:calc(var(--truck-columns, 4) * 22px + (var(--truck-columns, 4) - 1) * 3px);
    gap:3px;
  }

  .truck-logo{
    width:22px;
    height:22px;
  }

  .truck-logo span{
    font-size:10px;
  }

  .drawer.ops-modal{
    width:min(94vw, 94vw);
    max-height:calc(100vh - 16px);
    border-radius:20px;
  }

  .drawer.ops-modal .drawer-header{
    padding:12px 14px 10px;
  }

  .drawer.ops-modal .drawer-body{
    padding:12px 14px 10px;
  }

  .drawer.ops-modal .drawer-footer{
    padding:10px 14px;
  }

  .form-grid,
  .form-grid.camion{
    grid-template-columns:1fr;
  }

  .truck-obs-add{
    grid-template-columns:1fr;
    width:100%;
  }

  .row-line.truck-obs-row{
    grid-template-columns:1fr;
  }

  .truck-obs-state{
    width:100%;
    flex-basis:auto;
  }

  .op-camion-head{
    align-items:flex-start;
  }

  .op-camion-open{
    font-size:11px;
  }
}

`;

function useInjectStyles() {
  useEffect(() => {
    const id = "panel-operativo-dia-styles";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = EXTRA_STYLES;
    return () => tag.remove();
  }, []);
}

/* ================== HELPERS ================== */

const LOGOS_BASE = (process.env.PUBLIC_URL || "") + "/imagenesOperaciones";
const LOGOS_PRESET = [
  "inDescarga.jpg",
  "outCarga.jpg",
  "inArribado.jpg",
  "outArribado.jpg",
  "enProcesoDeDescarga.jpg",
  "enProcesoDeCarga.jpg",
  "descargaFinalizada.jpg",
  "cargaFinalizada.jpg",
  "operativaIngresoFinalizado.jpg",
  "operativaEgresoFinalizado.jpg",
];

const withExt = (name) => {
  if (!name) return "";
  if (/\.(png|jpg|jpeg|gif|svg|webp)$/i.test(name)) return name;
  return name + ".jpg";
};
const logoUrl = (name, dap = false) => `${LOGOS_BASE}/${withExt(getTruckLogoImageFile(name, dap))}`;

/** LOGOS que "cierran" camion y cierran contenedores */
/** LOGOS que "cierran" camion y cierran contenedores (AUTO-CLOSE) */
const AUTO_CLOSE_LOGOS = new Set(
  ["operativaIngresoFinalizado.jpg", "operativaEgresoFinalizado.jpg"].map((s) =>
    String(s).toLowerCase()
  )
);

const PENDING_LOGOS = new Set(
  ["inDescarga.jpg", "outCarga.jpg", "inArribado.jpg", "outArribado.jpg"].map((f) => f.toLowerCase())
);

const PROCESS_LOGOS = new Set(
  ["enProcesoDeCarga.jpg", "enProcesoDeDescarga.jpg"].map((f) => f.toLowerCase())
);

const FINAL_LOGOS = new Set(
  [
    "descargaFinalizada.jpg",
    "cargaFinalizada.jpg",
    "operativaIngresoFinalizado.jpg",
    "operativaEgresoFinalizado.jpg",
  ].map((f) => f.toLowerCase())
);

const shouldAutoCloseLogo = (nombre) => {
  const raw = String(nombre || "").trim().toLowerCase();
  if (!raw) return false;

  const full = withExt(raw).toLowerCase();           // ej: xxx.jpg
  const noExt = raw.replace(/\.(png|jpg|jpeg|webp|gif|svg)$/i, ""); // ej: xxx

  return AUTO_CLOSE_LOGOS.has(full) || AUTO_CLOSE_LOGOS.has(noExt);
};


function estadoCamionPorLogo(nombre) {
  const full = withExt(nombre || "").toLowerCase();
  if (!full) return "pendiente";

  // ? prioridad auto-close
  if (AUTO_CLOSE_LOGOS.has(full)) return "finalizado";

  if (FINAL_LOGOS.has(full)) return "finalizado";
  if (PROCESS_LOGOS.has(full)) return "proceso";
  if (PENDING_LOGOS.has(full)) return "pendiente";
  return "pendiente";
}

const logoEnProcesoParaMovimiento = (tipo) =>
  String(tipo || "").toLowerCase() === "egreso"
    ? "enProcesoDeCarga.jpg"
    : "enProcesoDeDescarga.jpg";

function getOperacionObservacion(op) {
  const match = [
    op?.comentarioGeneral,
    op?.observacion,
    op?.comentario,
    op?.textoComentario,
  ].find((value) => typeof value === "string" && value.trim());

  return match ? match.trim() : "";
}

const SIEL_LABEL = "SIEL";
const FACTURACION_LABEL = "Facturaci\u00f3n";
const CIEL_SIEL_LABEL = `Control ${SIEL_LABEL} / ${FACTURACION_LABEL}`;
const getCamionByIndex = (op, camionIndex) =>
  (Array.isArray(op?.camiones) ? op.camiones : []).find(
    (c) => Number(c?.index) === Number(camionIndex)
  ) || null;

async function loadLogoList() {
  try {
    const r = await fetch(`${LOGOS_BASE}/manifest.json`, { cache: "no-cache" });
    if (r.ok) {
      const arr = await r.json();
      if (Array.isArray(arr) && arr.length) {
        return arr.filter((f) => typeof f === "string");
      }
    }
  } catch {}
  return LOGOS_PRESET;
}

const fechaUTC = (f = new Date()) => new Date(f).toISOString().slice(0, 10);
const sumarDiasFecha = (yyyyMmDd, dias) => {
  const date = new Date(`${yyyyMmDd}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + dias);
  return fechaUTC(date);
};
const HOY = operationalToday();

/* Axios */
const apiOps = createApiClient({
  baseURL:
    process.env.REACT_APP_OPS_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "https://dnbinderapi.pruebasistemasgenius.com",
  headers: { "Content-Type": "application/json" },
});

const clientesBaseURL =
  process.env.REACT_APP_CLIENTES_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_OPS_BASE_URL ||
  "https://dnbinderapi.pruebasistemasgenius.com";

const apiClientes = createApiClient({
  baseURL: clientesBaseURL,
  headers: { "Content-Type": "application/json" },
});

const OPS = {
  LISTAR: "/api/operaciones/listar",
  CREAR_OP: (f) => `/api/operaciones/dia/${encodeURIComponent(f)}/operaciones`,
  EDITAR_OP: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(f)}/operaciones/${encodeURIComponent(
      id
    )}`,
  BORRAR_OP: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(f)}/operaciones/${encodeURIComponent(
      id
    )}`,
  CREAR_OBS: (f) => `/api/operaciones/dia/${encodeURIComponent(f)}/observaciones`,
  EDITAR_OBS: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(f)}/observaciones/${encodeURIComponent(
      id
    )}`,
  BORRAR_OBS: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(f)}/observaciones/${encodeURIComponent(
      id
    )}`,
  POSPONER_OP: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(
      f
    )}/operaciones/${encodeURIComponent(id)}/posponer`,
};

const CLIENTES = {
  LISTAR: "/api/clientes/listar",
};

export function getTrasladoTooltip(operacion, fechaDestino) {
  return getOperationTransfers(operacion, fechaDestino).incoming;
}

const normalizeClienteLookupValue = (value) =>
  String(value ?? "").trim().toLocaleLowerCase("es");

const getClienteDisplayName = (cliente) => {
  if (!cliente) return "";
  const codigo = String(cliente.codCliente || "").trim();
  const nombres = [
    cliente.razonSocial,
    cliente.nombreCliente,
    cliente.nombreFantasia,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return (
    nombres.find(
      (nombre) =>
        normalizeClienteLookupValue(nombre) !==
        normalizeClienteLookupValue(codigo)
    ) ||
    nombres[0] ||
    codigo
  );
};

const findClienteForOperacion = (operacion, clientes) => {
  const lista = Array.isArray(clientes) ? clientes : [];
  const id = normalizeClienteLookupValue(operacion?.clienteId);
  const codigo = normalizeClienteLookupValue(
    operacion?.clienteCodigo || operacion?.codCliente
  );
  const nombre = normalizeClienteLookupValue(
    operacion?.clienteNombre || operacion?.nombreCliente
  );

  if (id) {
    const byId = lista.find(
      (cliente) => normalizeClienteLookupValue(cliente?._id) === id
    );
    if (byId) return byId;
  }

  const codeCandidate = codigo || nombre;
  if (codeCandidate) {
    const byCode = lista.find(
      (cliente) =>
        normalizeClienteLookupValue(cliente?.codCliente) === codeCandidate
    );
    if (byCode) return byCode;
  }

  if (!nombre) return null;
  return (
    lista.find((cliente) =>
      [cliente?.nombreCliente, cliente?.razonSocial, cliente?.nombreFantasia].some(
        (value) => normalizeClienteLookupValue(value) === nombre
      )
    ) || null
  );
};

/* ================== OCUPACION API (con fallback localStorage) ================== */

const LS_KEY_OCU = "ops_ocupaciones_v1";
const buildOcupacionesStorageKey = (deposito) =>
  `${LS_KEY_OCU}:${String(deposito || "").trim().toLowerCase() === "binder2" ? "binder2" : "binder1"}`;

function normalizeOcupacion(item) {
  if (!item) return null;
  return {
    _id: item._id || item.id || item.uuid || `${item.nombre}-${item.ocupacionTotal}`,
    nombre: item.nombre || item.sucursal || "Sin nombre",
    ocupacionTotal: Number(item.ocupacionTotal ?? item.total ?? 0),
    ocupadas: Number(item.ocupadas ?? item.ocupacionOcupada ?? 0),
    visible: item.visible !== false,
  };
}

function readStoredOcupaciones(storageKey) {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const arr = raw ? JSON.parse(raw) : [];
    return (arr || []).map(normalizeOcupacion).filter(Boolean);
  } catch {
    return [];
  }
}

async function apiListOcupaciones(deposito) {
  const params = { deposito, _ts: Date.now() };
  const tries = [
    () => apiOps.get("/api/ocupacion", { params }),
    () => apiOps.get("/api/ocupacion/listar", { params }),
    () => apiOps.get("/api/ocupaciones", { params }),
    () => apiOps.get("/api/ocupaciones/listar", { params }),
  ];

  for (const fn of tries) {
    try {
      const r = await fn();
      const arr = Array.isArray(r.data) ? r.data : r.data?.data || [];
      return arr.map(normalizeOcupacion).filter(Boolean);
    } catch {}
  }
  throw new Error("No se pudo listar ocupaciones (API).");
}

/* ================== COMPONENTES AUXILIARES ================== */

export function SelectCliente({
  value,
  onChange,
  onSelect,
  inputRef,
  onEnterNext,
  highlightOnEntry = false,
  clientes = [],
  loading = false,
  error = "",
  onRetry,
}) {
  const [query, setQuery] = useState(value || "");
  const [selected, setSelected] = useState(null);
  const opts = useMemo(() => {
    const normalize = (text) => normalizeClienteLookupValue(text).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const search = normalize(query);
    return clientes.filter((cliente) => !search || [
      cliente.nombreCliente, cliente.razonSocial, cliente.nombreFantasia, cliente.codCliente, cliente.CUIT,
    ].some((value) => normalize(value).includes(search)));
  }, [clientes, query]);
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownSpace, setDropdownSpace] = useState(0);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => setQuery(value || ""), [value]);

  useLayoutEffect(() => {
    if (!open) {
      setDropdownSpace(0);
      return undefined;
    }

    const updateDropdownSpace = () => {
      const nextHeight = dropdownRef.current?.offsetHeight || 0;
      setDropdownSpace(nextHeight > 0 ? nextHeight + 8 : 0);
    };

    updateDropdownSpace();

    if (typeof ResizeObserver === "undefined" || !dropdownRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver(updateDropdownSpace);
    observer.observe(dropdownRef.current);
    return () => observer.disconnect();
  }, [open, loading, opts.length, query]);

  const select = (cli) => {
    const nombre = getClienteDisplayName(cli);
    setSelected(cli || null);
    setQuery(nombre);
    onChange?.(nombre);
    onSelect?.(cli);
    setOpen(false);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const selectedName = getClienteDisplayName(selected);
    if (selectedName) {
      setQuery(selectedName);
      onChange?.(selectedName);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (open && (loading || error || opts.length === 0)) return;
    let cli = selected || null;
    if (open && opts.length > 0) {
      cli = opts[0];
      select(cli);
    } else if (getClienteDisplayName(selected)) {
      const selectedName = getClienteDisplayName(selected);
      setQuery(selectedName);
      onChange?.(selectedName);
    }
    onEnterNext?.(cli);
  };

  return (
    <div
      className="field cliente-field"
      ref={wrapRef}
      style={open && dropdownSpace ? { marginBottom: 10 + dropdownSpace } : undefined}
    >
      <label>Cliente</label>
      <div
        className={`input-search input-search--xs ${
          highlightOnEntry || isFocused ? "input-search--active" : ""
        }`.trim()}
        onClick={() => setOpen(true)}
      >
        <FiSearch />
        <input
          ref={inputRef}
          value={query}
          placeholder="Buscar cliente…"
          onChange={(e) => {
            setSelected(null);
            onSelect?.(null);
            setQuery(e.target.value);
            onChange?.(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            setOpen(true);
          }}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
        />
      </div>

      {open && (
        <div
          ref={dropdownRef}
          className="panel cliente-dropdown"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading && <div className="empty">Buscando…</div>}
          {!loading && error && (
            <div className="empty" role="alert">
              {error}{" "}
              <button type="button" className="btn ghost" onClick={onRetry}>Reintentar</button>
            </div>
          )}
          {!loading && !error && opts.length === 0 && (
            <div className="empty">{clientes.length ? "Sin resultados" : "No hay clientes cargados."}</div>
          )}
          {!loading && !error &&
            opts.map((c) => (
              <button
                type="button"
                key={c._id || `${c.nombreCliente}-${c.CUIT || ""}`}
                className="cliente-opt"
                onClick={() => select(c)}
              >
                <div className="cliente-opt-main">
                  <span>
                    <b>{getClienteDisplayName(c)}</b>
                    {c.codCliente ? ` · ${c.codCliente}` : ""}
                  </span>
                  {c.CUIT && (
                    <span className="cliente-opt-cuit">{c.CUIT}</span>
                  )}
                </div>
                {c.direccion && (
                  <div className="cliente-opt-sub">{c.direccion}</div>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function LogoSelect({ value, onChange, logos, getButtonRef, onEnterNext, dap = false }) {
  return (
    <div className="field logo-select-field">
      <label>Logo</label>

      <div className="logo-grid" style={{ "--logo-count": Math.max(1, logos.length) }}>
        {logos.map((file) => {
          const selected = file === value;
          return (
            <button
              key={file}
              type="button"
              ref={(el) => getButtonRef?.(file, el)}
              className={`logo-item ${selected ? "selected" : ""}`}
              aria-pressed={selected}
              onClick={() => onChange?.(file)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                onChange?.(file);
                onEnterNext?.();
              }}
              title={getTruckLogoLabel(file)} aria-label={getTruckLogoLabel(file)}
            >
              <img src={logoUrl(file, dap)} alt="" />
            </button>
          );
        })}

        {!logos.length && (
          <div className="logo-empty">
            <FiImage size={24} />
            <span>Sin logos</span>
          </div>
        )}
      </div>
    </div>
  );
}

function TruckLogoVisual({ imageName, label, dap = false }) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageName, dap]);

  if (!imageName || imageFailed) {
    return <FiTruck aria-label={label || "Camion sin logo"} />;
  }

  return (
    <img
      src={logoUrl(imageName, dap)}
      alt=""
      onError={() => setImageFailed(true)}
    />
  );
}

function Drawer({ open, onClose, children, className = "" }) {
  return (
    <>
      <div className={`drawer-overlay ${open ? "show" : ""}`} onClick={onClose} />
      <div className={`drawer ${open ? "open" : ""} ${className}`.trim()} role="dialog">
        {children}
      </div>
    </>
  );
}

/* Normalizar camiones */

function normalizarCamiones(op) {
  let cams = Array.isArray(op.camiones) ? [...op.camiones] : [];
  const nCam = Math.max(1, Number(op.cantidadCamiones) || 0, cams.length);

  if (cams.length < nCam) {
    for (let i = cams.length; i < nCam; i++) {
      cams.push({
        index: i,
        dibujo: op.dibujo || "",
      });
    }
  }

  cams = cams.slice(0, nCam).map((c, i) => ({
    ...c,
    index: typeof c.index === "number" ? c.index : i,
    dibujo: c.dibujo || op.dibujo || "",
    pendiente: !!c.pendiente,
    finalizado: !!c.finalizado,
    ciel: !!c.ciel,
    finanzas: !!c.finanzas,
  }));

  return {
    ...op,
    cantidadCamiones: nCam,
    camiones: cams,
  };
}

/** ? aplica regla: si logo auto-close -> cerrar contenedores de ese camión + flag finalizado */
function aplicarAutoCloseEnOp(opNorm, camionIndex, nuevoLogo) {
  const autoClose = shouldAutoCloseLogo(nuevoLogo);
  if (!autoClose) return opNorm;

  const camiones = Array.isArray(opNorm.camiones) ? [...opNorm.camiones] : [];
  const idx = camiones.findIndex((c) => c.index === camionIndex);
  if (idx >= 0) {
    camiones[idx] = { ...camiones[idx], finalizado: true, pendiente: false };
  }

  const conts = Array.isArray(opNorm.Contenedores) ? [...opNorm.Contenedores] : [];
  const Contenedores = conts.map((c) =>
    (c.camionIndex || 0) === camionIndex ? { ...c, estado: "Finalizado" } : c
  );

  return { ...opNorm, camiones, Contenedores };
}

/* CONFIG PANEL DIARIO */

const PANELS_DIA = [
  { id: "fi-in", titulo: "In Impo", sub: "Ingresos", tipoOperacion: "FiscalImpo", tipo: "Ingreso" },
  { id: "fi-eg", titulo: "Out Impo", sub: "Egresos", tipoOperacion: "FiscalImpo", tipo: "Egreso" },
  { id: "fe-in", titulo: "In Expo", sub: "Ingresos", tipoOperacion: "FiscalExpo", tipo: "Ingreso" },
  { id: "fe-eg", titulo: "Out Expo", sub: "Egresos", tipoOperacion: "FiscalExpo", tipo: "Egreso" },
  { id: "na-in", titulo: "In Nac", sub: "Ingresos", tipoOperacion: "Nacional", tipo: "Ingreso" },
  { id: "na-eg", titulo: "Out Nac", sub: "Egresos", tipoOperacion: "Nacional", tipo: "Egreso" },
  { id: "obs-fi", titulo: "Observaciones/Anotaciones Fiscal", sub: "Notas del día", observAmbito: "Fiscal" },
  { id: "obs-na", titulo: "Observaciones/Anotaciones Nacional", sub: "Notas del día", observAmbito: "Nacional" },
];

function normalizarAmbitoObs(ambito) {
  const raw = String(ambito || "").trim().toLowerCase();
  if (raw === "fiscal") return "Fiscal";
  if (raw === "nacional") return "Nacional";
  return ambito || "";
}

/* ================== COMPONENTE PRINCIPAL ================== */

export default function OperacionesDia() {
  useInjectStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { deposito, setDeposito, isAdmin, isFinanzas } = useUserId();
  const depositoFromRoute = readDepositoFromSearch(location.search);
  const depositoActual = resolveDepositoRoute({
    search: location.search,
    fallback: deposito,
  });
  const depositoLabel =
    String(depositoActual).trim().toLowerCase() === "binder2" ? "Binder II" : "Binder I";
  const params = new URLSearchParams(location.search);
  const fechaInicial = params.get("fecha") || HOY;
  const [fecha, setFecha] = useState(fechaInicial);
  const canToggleCielFinanzas = useCallback(
    (op) =>
      !!op &&
      op.tipo === "Egreso" &&
      (op.tipoOperacion === "FiscalImpo" || op.tipoOperacion === "FiscalExpo") &&
      (isAdmin || isFinanzas),
    [isAdmin, isFinanzas]
  );
  const ocupacionesStorageKey = useMemo(
    () => buildOcupacionesStorageKey(depositoActual),
    [depositoActual]
  );
  const toggleObs = async (obs) => {
  try {
    await apiOps.put(
      OPS.EDITAR_OBS(fecha, obs._id),
      { completada: !obs.completada, deposito: depositoActual } // deposito por si lo usás
    );
    notifySuccess(obs.completada ? "Observación marcada como pendiente." : "Observación completada.");
    fetchData();
  } catch (e) {
    console.error(e);
    alert("No pude actualizar la observación.");
  }
};

  const [dataDia, setDataDia] = useState({
    Operacion: [],
    observaciones_anotaciones: [],
  });
  const [loading, setLoading] = useState(false);
  const [ocupaciones, setOcupaciones] = useState([]);
  const [ocuLoading, setOcuLoading] = useState(false);
  const [clientesReferencia, setClientesReferencia] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(true);
  const [clientesError, setClientesError] = useState("");
  const [clientesReload, setClientesReload] = useState(0);

  const [crearOpen, setCrearOpen] = useState(false);
  const [tipoCrear, setTipoCrear] = useState("");
  const [movCrear, setMovCrear] = useState("");

  const [formOp, setFormOp] = useState({
    nombreCliente: "",
    cliente: null,
    dibujo: "",
    cantidadCamiones: 1,
    dap: false,
    siel: false,
    facturacion: false,
    Contenedores: [],
    comentarioGeneral: "",
    ocupacionId: "",
    ocupacionNombre: "",
  });
  const [formObs, setFormObs] = useState({ observacion: "", completada: false });

  const [contenedorCrear, setContenedorCrear] = useState({ nombre: "" });

  const [editObs, setEditObs] = useState(null);
  const [editOp, setEditOp] = useState(null);
  const [camionActivo, setCamionActivo] = useState(0);
  const [posponiendo, setPosponiendo] = useState(false);
  const posponiendoRef = useRef(false);
  const [contenedorDraft, setContenedorDraft] = useState({ nombre: "" });
  const [contenedorEditando, setContenedorEditando] = useState({
    index: null,
    nombre: "",
  });

  const [logos, setLogos] = useState([]);
  const logosCrear = useMemo(
    () => filtrarLogosPorMovimiento(logos, movCrear, { dap: formOp.dap, operationType: tipoCrear }),
    [logos, movCrear, formOp.dap, tipoCrear]
  );
  const logosEdicion = useMemo(
    () => filtrarLogosPorMovimiento(logos, editOp?.tipo, { dap: editOp?.dap, operationType: editOp?.tipoOperacion }),
    [logos, editOp?.tipo, editOp?.dap, editOp?.tipoOperacion]
  );

  const [hoverLogo, setHoverLogo] = useState(null);
  const logosHover = useMemo(
    () => filtrarLogosPorMovimiento(logos, hoverLogo?.op?.tipo, { dap: hoverLogo?.op?.dap, operationType: hoverLogo?.op?.tipoOperacion }),
    [logos, hoverLogo?.op?.tipo, hoverLogo?.op?.dap, hoverLogo?.op?.tipoOperacion]
  );
  const [modalOp, setModalOp] = useState(null); // operación seleccionada para ver contenedores/obs
  const [camionReturnTarget, setCamionReturnTarget] = useState(null);
  const [opObsDraft, setOpObsDraft] = useState({ texto: "" });
  const hoverTimerRef = useRef(null);
  const ocuRequestRef = useRef(0);

  const resolveOperacionCliente = useCallback(
    (operacion) => findClienteForOperacion(operacion, clientesReferencia),
    [clientesReferencia]
  );

  const resolveOperacionNombreCliente = useCallback(
    (operacion) => {
      const cliente = resolveOperacionCliente(operacion);
      return (
        getClienteDisplayName(cliente) ||
        String(operacion?.clienteNombre || operacion?.nombreCliente || "").trim() ||
        "Sin cliente"
      );
    },
    [resolveOperacionCliente]
  );

  const applyResolvedClienteIdentity = useCallback(
    (operacion) => {
      if (!operacion) return operacion;
      const cliente = resolveOperacionCliente(operacion);
      const nombreCliente = resolveOperacionNombreCliente(operacion);
      if (!cliente) return { ...operacion, nombreClienteVista: nombreCliente };

      return {
        ...operacion,
        nombreCliente,
        nombreClienteVista: nombreCliente,
        clienteId: String(cliente?._id || operacion?.clienteId || ""),
        clienteCodigo: String(
          cliente?.codCliente || operacion?.clienteCodigo || ""
        ),
        clienteNombre: nombreCliente,
      };
    },
    [resolveOperacionCliente, resolveOperacionNombreCliente]
  );

  const getTextoObservacionCamion = (item) => {
    const nombre = String(item?.nombre || "").trim();
    const obs = String(item?.obs || "").trim();
    if (nombre && obs) return `${nombre} - ${obs}`;
    return nombre || obs;
  };

  const clienteCrearInputRef = useRef(null);
  const ocupacionCrearSelectRef = useRef(null);
  const comentarioCrearRef = useRef(null);
  const cantidadCamionesCrearRef = useRef(null);
  const contenedorNombreCrearRef = useRef(null);
  const editObsInputRef = useRef(null);
  const logoCrearButtonsRef = useRef({});

  const setLogoCrearButtonRef = useCallback((file, el) => {
    if (!file) return;
    if (el) logoCrearButtonsRef.current[file] = el;
    else delete logoCrearButtonsRef.current[file];
  }, []);

  useEffect(() => {
    if (depositoFromRoute && depositoFromRoute !== deposito) {
      setDeposito(depositoFromRoute);
    }
  }, [depositoFromRoute, deposito, setDeposito]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const list = await loadLogoList();
      if (!cancel) setLogos(list);
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    let cancel = false;

    setClientesLoading(true);
    setClientesError("");
    apiClientes
      .get(CLIENTES.LISTAR)
      .then(({ data }) => {
        if (!cancel) setClientesReferencia(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (!cancel) {
          console.error("No se pudo cargar la referencia de clientes", error);
          setClientesReferencia([]);
          setClientesError("No se pudieron cargar los clientes. Verificá la conexión con el servidor.");
        }
      })
      .finally(() => {
        if (!cancel) setClientesLoading(false);
      });

    return () => {
      cancel = true;
    };
  }, [clientesReload]);

  const cargarOcupaciones = useCallback(async () => {
    const requestId = ++ocuRequestRef.current;
    setOcuLoading(true);
    try {
      const arr = await apiListOcupaciones(depositoActual);
      if (ocuRequestRef.current !== requestId) return;
      setOcupaciones(arr);
      localStorage.setItem(ocupacionesStorageKey, JSON.stringify(arr));
    } catch {
      if (ocuRequestRef.current === requestId) {
        setOcupaciones(readStoredOcupaciones(ocupacionesStorageKey));
      }
    } finally {
      if (ocuRequestRef.current === requestId) {
        setOcuLoading(false);
      }
    }
  }, [depositoActual, ocupacionesStorageKey]);

  useEffect(() => {
    setOcupaciones(readStoredOcupaciones(ocupacionesStorageKey));
  }, [ocupacionesStorageKey]);

  useEffect(() => {
    cargarOcupaciones();
  }, [cargarOcupaciones]);

  const fetchDia = useCallback(
    async (f) => {
      const { data } = await apiOps.get(OPS.LISTAR, {
        params: { fecha: f, deposito: depositoActual },
      });
      const registro = Array.isArray(data) ? data[0] || {} : data || {};
      return {
        Operacion: Array.isArray(registro.Operacion) ? registro.Operacion : [],
        observaciones_anotaciones: Array.isArray(registro.observaciones_anotaciones)
          ? registro.observaciones_anotaciones
          : [],
      };
    },
    [depositoActual]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchDia(fecha);
      setDataDia(r);
    } catch (e) {
      console.error(e);
      setDataDia({ Operacion: [], observaciones_anotaciones: [] });
    } finally {
      setLoading(false);
    }
  }, [fetchDia, fecha]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setEditOp(null);
    setHoverLogo(null);
    setModalOp(null);
    setCrearOpen(false);
    setEditObs(null);
  }, [fecha]);

  useEffect(() => {
    if (!crearOpen || movCrear === "Observacion") return;
    const t = setTimeout(() => {
      clienteCrearInputRef.current?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [crearOpen, movCrear]);

  useEffect(() => {
    if (!editObs) return;
    const t = setTimeout(() => {
      editObsInputRef.current?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [editObs]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    q.set("fecha", fecha);
    q.set("deposito", depositoActual);
    navigate(`/operacionesdia?${q.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, depositoActual]);

  const recargarDia = useCallback(async () => {
    const r = await fetchDia(fecha);
    setDataDia(r);
    return r;
  }, [fetchDia, fecha]);

  /* ==== QUICK UPDATE LOGO CAMIÓN (desde Win98) ==== */

  const quickActualizarCamion = async ({ opId, camionIndex, nuevoLogo }) => {
    try {
      const ops = dataDia.Operacion || [];
      const original = ops.find((o) => o._id === opId);
      if (!original) return;

      let opNorm = normalizarCamiones(original);

      let camiones = Array.isArray(opNorm.camiones) ? [...opNorm.camiones] : [];
      const idx = camiones.findIndex((c) => c.index === camionIndex);
      if (idx >= 0) {
        camiones[idx] = { ...camiones[idx], dibujo: nuevoLogo || "" };
      } else {
        camiones.push({ index: camionIndex, dibujo: nuevoLogo || "" });
      }
      opNorm = { ...opNorm, camiones };

      // ? si el logo cierra, cerramos contenedores de ese camión + flag
      opNorm = aplicarAutoCloseEnOp(opNorm, camionIndex, nuevoLogo);

      const payload = {
        ...opNorm,
        camiones: opNorm.camiones,
        Contenedores: opNorm.Contenedores, // ? importante
        deposito: depositoActual,
      };

      await apiOps.put(OPS.EDITAR_OP(fecha, opNorm._id), payload);
      notifySuccess("Estado del camión actualizado.");
      fetchData();
    } catch (e) {
      console.error(e);
      alert("No se pudo actualizar el camión.");
    }
  };

  const quickActualizarControlOperacion = useCallback(
    async ({ opId, field, value }) => {
      const op = (dataDia.Operacion || []).find((item) => String(item._id) === String(opId));
      if (!canToggleCielFinanzas(op) || !["siel", "facturacion"].includes(field)) return;
      try {
        await apiOps.put(OPS.EDITAR_OP(fecha, opId), { [field]: !!value, deposito: depositoActual });
        notifySuccess(`${field === "siel" ? "SIEL actualizado" : "Facturación actualizada"} correctamente.`);
        await fetchData();
      } catch (error) {
        console.error(error);
        alert("No se pudo actualizar el control de la operación.");
      }
    },
    [canToggleCielFinanzas, dataDia.Operacion, depositoActual, fecha, fetchData]
  );

  /* ==== CREAR ==== */

  const abrirCrearOperacion = (tipoOperacion, tipo) => {
    setTipoCrear(tipoOperacion);
    setMovCrear(tipo);
    setFormOp({
      nombreCliente: "",
      cliente: null,
      dibujo: "",
      cantidadCamiones: 1,
      dap: false,
      siel: false,
      facturacion: false,
      Contenedores: [],
      comentarioGeneral: "",
      ocupacionId: "",
      ocupacionNombre: "",
    });
    setFormObs({ observacion: "" });
    setContenedorCrear({ nombre: "" });
    setCrearOpen(true);
  };

  const abrirCrearObservacion = (ambito) => {
  setTipoCrear(ambito);
  setMovCrear("Observacion");
  setFormObs({ observacion: "", completada: false });
  setCrearOpen(true);
};

  const agregarContenedorCrear = () => {
    if (!contenedorCrear.nombre.trim()) return false;
    const nuevo = { nombre: contenedorCrear.nombre.trim(), obs: "" };
    setFormOp((prev) => ({
      ...prev,
      Contenedores: [...(prev.Contenedores || []), nuevo],
    }));
    setContenedorCrear({ nombre: "" });
    return true;
  };

  const crear = async () => {
    try {
      if (movCrear === "Observacion") {
        const payload = {
    observacion: formObs.observacion,
    completada: !!formObs.completada,
    ambito: tipoCrear, // "Fiscal" / "Nacional" (tu backend lo pasa a lower al guardar)
    fecha,
    deposito: depositoActual,
  };
        await apiOps.post(OPS.CREAR_OBS(fecha), payload);
      } else {
        const nCam = Number(formOp.cantidadCamiones);
        if (!Number.isSafeInteger(nCam) || nCam < 1) {
          alert("Ingresá una cantidad de camiones entera, mayor o igual a 1.");
          cantidadCamionesCrearRef.current?.focus();
          return;
        }
        const plantilla = Array.isArray(formOp.Contenedores)
          ? formOp.Contenedores.map((c) => ({ ...c, estado: c.estado || "Completar" }))
          : [];
        const clienteSeleccionado = formOp?.cliente || null;
        const nombreCliente =
          getClienteDisplayName(clienteSeleccionado) || formOp.nombreCliente || "";

        const payload = {
          ...formOp,
          dibujo: formOp.dibujo || "",
          nombreCliente,
          clienteId: String(clienteSeleccionado?._id || ""),
          clienteCodigo: String(clienteSeleccionado?.codCliente || ""),
          clienteNombre: nombreCliente,
          comentarioGeneral: formOp.comentarioGeneral || "",
          tipoOperacion: tipoCrear,
          tipo: movCrear,
          fecha,
          Usuario: "admin",
          deposito: depositoActual,
          cantidadCamiones: nCam,
          Contenedores: plantilla,
        };

        await apiOps.post(OPS.CREAR_OP(fecha), payload);
      }
      notifySuccess(movCrear === "Observacion" ? "Observación creada correctamente." : "Operación creada correctamente.");
      setCrearOpen(false);
      fetchData();
    } catch (e) {
      console.error(e);
      alert("No pude crear la operación.");
    }
  };

  const abrirEditarObservacion = (obs) => {
    if (!obs?._id) return;
    setEditObs({
      ...obs,
      observacion: obs.observacion || "",
      completada: !!obs.completada,
      ambito: normalizarAmbitoObs(obs.ambito),
    });
  };

  const cerrarEditarObservacion = () => {
    setEditObs(null);
  };

  const guardarObservacionEditada = async () => {
    if (!editObs?._id) return;
    try {
      await apiOps.put(OPS.EDITAR_OBS(fecha, editObs._id), {
        observacion: editObs.observacion || "",
        completada: !!editObs.completada,
        ambito: editObs.ambito,
        deposito: depositoActual,
      });
      notifySuccess("Observación actualizada correctamente.");
      cerrarEditarObservacion();
      await recargarDia();
    } catch (e) {
      console.error(e);
      alert("No pude guardar la observación.");
    }
  };

  const eliminarObservacion = async (obs) => {
    if (!obs?._id) return;
    if (!window.confirm("¿Eliminar esta observación?")) return;
    try {
      await apiOps.delete(OPS.BORRAR_OBS(fecha, obs._id), {
        params: { deposito: depositoActual },
      });
      notifySuccess("Observación eliminada correctamente.");
      if (editObs && String(editObs._id) === String(obs._id)) {
        cerrarEditarObservacion();
      }
      await recargarDia();
    } catch (e) {
      console.error(e);
      alert("No pude eliminar la observación.");
    }
  };

  /* ==== EDICIÓN CONTENEDORES ==== */

  const agregarContenedorCamion = () => {
    const observacion = contenedorDraft.nombre.trim();
    if (!editOp || !observacion) return;
    const nuevo = {
      nombre: observacion,
      obs: "",
      estado: "Completar",
      camionIndex: camionActivo,
    };
    setEditOp((op) => ({
      ...op,
      Contenedores: [...(op.Contenedores || []), nuevo],
    }));
    setContenedorDraft({ nombre: "" });
    setContenedorEditando({ index: null, nombre: "" });
  };

  const eliminarContenedor = (idxDel) => {
    setContenedorEditando((prev) => {
      if (prev.index === idxDel) return { index: null, nombre: "" };
      if (prev.index !== null && prev.index > idxDel) {
        return { ...prev, index: prev.index - 1 };
      }
      return prev;
    });
    setEditOp((op) => ({
      ...op,
      Contenedores: (op.Contenedores || []).filter((_, idx) => idx !== idxDel),
    }));
  };

  const editarContenedor = (idx) => {
    const actual = (editOp?.Contenedores || [])[idx];
    setContenedorEditando({
      index: idx,
      nombre: getTextoObservacionCamion(actual),
    });
  };

  const guardarEdicionContenedor = (idx) => {
    const observacion = contenedorEditando.nombre.trim();
    if (contenedorEditando.index !== idx || !observacion) return;

    setEditOp((op) => {
      const arr = [...(op.Contenedores || [])];
      arr[idx] = {
        ...(arr[idx] || {}),
        nombre: observacion,
        obs: "",
      };
      return { ...op, Contenedores: arr };
    });
    setContenedorEditando({ index: null, nombre: "" });
  };

  const cambiarEstadoContenedor = (idx, estado) => {
    setEditOp((op) => {
      const arr = [...(op.Contenedores || [])];
      arr[idx] = { ...(arr[idx] || {}), estado };

      if (estado !== "Completar") {
        return { ...op, Contenedores: arr };
      }

      const camionIndex = Number(arr[idx]?.camionIndex || 0);
      const camiones = [...(op.camiones || [])];
      const camionPos = camiones.findIndex(
        (camion) => Number(camion?.index) === camionIndex
      );
      const logoActual =
        (camionPos >= 0 ? camiones[camionPos]?.dibujo : "") || op.dibujo || "";

      if (estadoCamionPorLogo(logoActual) !== "finalizado") {
        return { ...op, Contenedores: arr };
      }

      const dibujoEnProceso = logoEnProcesoParaMovimiento(op.tipo);
      const camionActual = camionPos >= 0 ? camiones[camionPos] : { index: camionIndex };
      const camionReabierto = {
        ...camionActual,
        index: camionIndex,
        dibujo: dibujoEnProceso,
        pendiente: true,
        finalizado: false,
        fechaFinalizado: null,
      };

      if (camionPos >= 0) camiones[camionPos] = camionReabierto;
      else camiones.push(camionReabierto);

      return {
        ...op,
        dibujo:
          estadoCamionPorLogo(op.dibujo || "") === "finalizado"
            ? dibujoEnProceso
            : op.dibujo,
        camiones,
        Contenedores: arr,
        EstadoCompleto: false,
        esPendiente: true,
      };
    });
  };

  const prepararOperacionCamionParaGuardar = () => {
    if (!editOp) return;
    let opFinal = editOp;

    if (contenedorEditando.index !== null) {
      const observacionPendiente = contenedorEditando.nombre.trim();
      if (!observacionPendiente) {
        alert("La observación no puede estar vacía.");
        return;
      }

      opFinal = {
        ...editOp,
        Contenedores: (editOp.Contenedores || []).map((item, idx) =>
          idx === contenedorEditando.index
            ? { ...item, nombre: observacionPendiente, obs: "" }
            : item
        ),
      };
    }

    const observacionSinAgregar = contenedorDraft.nombre.trim();
    if (observacionSinAgregar) {
      opFinal = {
        ...opFinal,
        Contenedores: [
          ...(opFinal.Contenedores || []),
          {
            nombre: observacionSinAgregar,
            obs: "",
            estado: "Completar",
            camionIndex: camionActivo,
          },
        ],
      };
    }

    return normalizarCamiones(opFinal);
  };

  const persistirCambiosCamion = async () => {
    const opConCierre = prepararOperacionCamionParaGuardar();
    if (!opConCierre) return null;

    const payload = {
      ...opConCierre,
      dibujo: opConCierre.dibujo || "",
      nombreCliente: opConCierre.nombreCliente || "",
      comentarioGeneral: opConCierre.comentarioGeneral || "",
      camiones: opConCierre.camiones,
      Contenedores: opConCierre.Contenedores,
      deposito: depositoActual,
    };

    await apiOps.put(OPS.EDITAR_OP(fecha, opConCierre._id), payload);
    setEditOp(opConCierre);
    setContenedorDraft({ nombre: "" });
    setContenedorEditando({ index: null, nombre: "" });
    return opConCierre;
  };

  const guardarCamion = async () => {
    if (!editOp) return;
    try {
      const guardada = await persistirCambiosCamion();
      if (!guardada) return;
      notifySuccess(`Camión ${camionActivo + 1} actualizado correctamente.`);
      closeCamionEditor();
      fetchData();
    } catch (e) {
      console.error(e);
      alert("No pude guardar los cambios.");
    }
  };

  const borrarCamionIndividual = async () => {
    if (!editOp) return;
    if (!window.confirm(`¿Eliminar solo el camión ${camionActivo + 1}?`)) return;
    try {
      await apiOps.delete(
        `/api/operaciones/dia/${encodeURIComponent(fecha)}/operaciones/${encodeURIComponent(
          editOp._id
        )}/camiones/${camionActivo}`,
        { params: { deposito: depositoActual } }
      );
      notifySuccess("Camión eliminado correctamente.");
      closeCamionEditor();
      fetchData();
    } catch (e) {
      console.error(e);
      alert(operationDeleteError(e, "el camión"));
    }
  };

  const cerrarTodosContenedoresCamion = () => {
    if (!editOp) return;
    setEditOp((op) => ({
      ...op,
      Contenedores: (op.Contenedores || []).map((c) =>
        (c.camionIndex || 0) === camionActivo ? { ...c, estado: "Finalizado" } : c
      ),
    }));
  };

  const posponerOperacion = async () => {
    if (!editOp || posponiendoRef.current) return;
    const destino = sumarDiasFecha(fecha, 1);
    if (
      !window.confirm(
        "¿Pasar esta operativa a mañana? " +
          "Las observaciones finalizadas quedarán registradas en el día actual."
      )
    )
      return;
    posponiendoRef.current = true;
    setPosponiendo(true);
    try {
      const operacionGuardada = await persistirCambiosCamion();
      if (!operacionGuardada) return;

      await apiOps.post(OPS.POSPONER_OP(fecha, operacionGuardada._id), {
        deposito: depositoActual,
        fechaDestino: destino,
      });
      notifySuccess(`Traslado al ${destino.split("-").reverse().join("/")} guardado correctamente.`);
      closeCamionEditor();
      await fetchData();
    } catch (e) {
      console.error("No se pudo pasar la operativa al día siguiente:", e);
      alert(
        e?.response?.data?.error ||
          "No se pudo pasar la operativa al día siguiente. Intentá nuevamente."
      );
    } finally {
      posponiendoRef.current = false;
      setPosponiendo(false);
    }
  };

  const abrirDetalleOperacion = (op) => {
    const opNorm = applyResolvedClienteIdentity(normalizarCamiones(op));
    setModalOp(opNorm);
    setOpObsDraft({
      texto: getOperacionObservacion(opNorm),
    });
  };

  const guardarObsOperacion = async () => {
    if (!modalOp) return;
    try {
      await apiOps.put(OPS.EDITAR_OP(fecha, modalOp._id), {
        comentarioGeneral: opObsDraft.texto || "",
        deposito: depositoActual,
      });

      const r = await recargarDia();
      const updated = (r.Operacion || []).find((o) => String(o._id) === String(modalOp._id));
      setModalOp(updated ? normalizarCamiones(updated) : null);
      notifySuccess("Observación de la operación actualizada correctamente.");
    } catch (e) {
      console.error(e);
      alert("No pude guardar la observación.");
    }
  };

  const borrarOperacionDesdeModal = async () => {
    if (!modalOp) return;
    if (!window.confirm("¿Eliminar la operación completa?")) return;
    try {
      await apiOps.delete(OPS.BORRAR_OP(fecha, modalOp._id), {
        params: { deposito: depositoActual },
      });
      setModalOp(null);
      notifySuccess("Operación eliminada correctamente.");
      await recargarDia();
    } catch (e) {
      console.error(e);
      alert(operationDeleteError(e, "la operación"));
    }
  };

  const agregarNuevoCamion = async (op) => {
    try {
      const opNorm = normalizarCamiones(op);
      const nuevoIndex = (opNorm.camiones || []).length;
      const camiones = [...(opNorm.camiones || [])];
      camiones.push({
        index: nuevoIndex,
        pendiente: true,
        finalizado: false,
        dibujo: opNorm.dibujo || "",
        ciel: false,
        finanzas: false,
        fechaFinalizado: null,
        pospuestoA: null,
        origenIndex: null,
      });

      const payload = {
        ...opNorm,
        cantidadCamiones: camiones.length,
        camiones,
        Contenedores: opNorm.Contenedores || [],
        deposito: depositoActual,
      };

      await apiOps.put(OPS.EDITAR_OP(fecha, opNorm._id), payload);

      notifySuccess("Camión agregado correctamente.");
      const r = await recargarDia();
      if (modalOp && String(modalOp._id) === String(opNorm._id)) {
        const updated = (r.Operacion || []).find((x) => String(x._id) === String(opNorm._id));
        setModalOp(updated ? normalizarCamiones(updated) : null);
      }
    } catch (e) {
      console.error(e);
      alert("No pude agregar un nuevo camión.");
    }
  };

  /* ==== HOVER / POPUP ==== */

  const clearHoverTimeout = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const scheduleHideHover = () => {
    clearHoverTimeout();
    hoverTimerRef.current = setTimeout(() => setHoverLogo(null), 180);
  };

  const showHoverCamion = (event, op, camionIndex, logoVal) => {
    clearHoverTimeout();
    const rect = event.currentTarget.getBoundingClientRect();
    const opNorm = normalizarCamiones(op);
    const camion = getCamionByIndex(opNorm, camionIndex);

    const contenedores = (opNorm.Contenedores || [])
      .map((c, idx) => ({ ...c, __idx: idx }))
      .filter((c) => (c.camionIndex || 0) === camionIndex);

    const estado = estadoCamionPorLogo(logoVal || opNorm.dibujo);

    setHoverLogo({
      tipo: "camion",
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
      opId: opNorm._id,
      camionIndex,
      logo: logoVal || "",
      estado,
      cliente: resolveOperacionNombreCliente(opNorm),
      contenedores,
      op: opNorm,
      ciel: !!camion?.ciel,
      siel: getOperationSiel(opNorm),
      facturacion: getOperacionFacturacion(opNorm),
      canToggleCielFinanzas: canToggleCielFinanzas(opNorm),
    });
  };

  const showHoverOperacion = (event, op) => {
    clearHoverTimeout();
    const rect = event.currentTarget.getBoundingClientRect();
    const opNorm = normalizarCamiones(op);

    const cant = Math.max(1, Number(opNorm.cantidadCamiones || 1));
    const camiones = Array.from({ length: cant }).map((_, idx) => {
      const camion = (opNorm.camiones || []).find((c) => c.index === idx) || null;
      const logo = camion?.dibujo || opNorm.dibujo || "";
      const estado = estadoCamionPorLogo(logo);
      const conts = (opNorm.Contenedores || [])
        .map((c, i) => ({ ...c, __idx: i }))
        .filter((c) => Number(c.camionIndex || 0) === Number(idx));

      return {
        idx,
        logo,
        estado,
        contenedores: conts,
        ciel: !!camion?.ciel,
      };
    });

    setHoverLogo({
      tipo: "operacion",
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
      opId: opNorm._id,
      cliente: resolveOperacionNombreCliente(opNorm),
      op: opNorm,
      camiones,
      siel: getOperationSiel(opNorm),
      facturacion: getOperacionFacturacion(opNorm),
      canToggleCielFinanzas: canToggleCielFinanzas(opNorm),
    });
  };

  const handleHoverLogo = async (val) => {
    if (!hoverLogo || hoverLogo.tipo !== "camion") return;
    setHoverLogo((h) => (h ? { ...h, logo: val, estado: estadoCamionPorLogo(val) } : h));
    await quickActualizarCamion({
      opId: hoverLogo.opId,
      camionIndex: hoverLogo.camionIndex,
      nuevoLogo: val,
    });
  };

  const handleHoverControl = async (field, value) => {
    if (!hoverLogo?.canToggleCielFinanzas) return;
    setHoverLogo((current) => current ? { ...current, [field]: !!value,
      op: withOperationControl(current.op, field, value) } : current);
    await quickActualizarControlOperacion({
      opId: hoverLogo.opId, field, value,
    });
  };

  const closeCamionEditor = useCallback(() => {
    setEditOp(null);
    setContenedorDraft({ nombre: "" });
    setContenedorEditando({ index: null, nombre: "" });
    setCamionReturnTarget(null);
  }, []);

  const volverAListadoOperacion = useCallback(() => {
    if (!camionReturnTarget?.op) {
      closeCamionEditor();
      return;
    }

    const opNorm = applyResolvedClienteIdentity(
      normalizarCamiones(camionReturnTarget.op)
    );
    setEditOp(null);
    setCamionReturnTarget(null);
    setModalOp(opNorm);
    setOpObsDraft({
      texto: getOperacionObservacion(opNorm),
    });
  }, [applyResolvedClienteIdentity, camionReturnTarget, closeCamionEditor]);

  const openCamion = (op, camionIndex, options = {}) => {
    setHoverLogo(null);
    const opNorm = applyResolvedClienteIdentity(normalizarCamiones(op));
    setContenedorDraft({ nombre: "" });
    setContenedorEditando({ index: null, nombre: "" });
    setEditOp(opNorm);
    setCamionActivo(camionIndex);
    if (options?.returnToOperationModal) {
      setCamionReturnTarget({ type: "operation-modal", op: opNorm });
      return;
    }
    setCamionReturnTarget(null);
  };

  const puedeEditarCielFinanzas = canToggleCielFinanzas(editOp);

  const setOperacionControl = (field, value) => {
    if (!editOp || !canToggleCielFinanzas(editOp)) return;
    setEditOp((operation) => withOperationControl(operation, field, value));
  };
  const sielActivo = getOperationSiel(editOp);
  const facturacionActiva = getOperacionFacturacion(editOp);

  const resolveOcupacionId = (op) => {
    if (!op) return "";
    if (op.ocupacionId) return String(op.ocupacionId);
    const nombre = (op.ocupacionNombre || "").trim();
    if (!nombre) return "";
    const found = (ocupaciones || []).find((o) => (o?.nombre || "").trim() === nombre);
    return found ? String(found._id) : "";
  };

  const resolveOcupacionNombre = (op) => {
    if (!op) return "";
    const nombre = (op.ocupacionNombre || "").trim();
    if (nombre) return nombre;
    const id = op.ocupacionId ? String(op.ocupacionId) : "";
    if (!id) return "";
    const found = (ocupaciones || []).find((o) => String(o?._id) === id);
    return found?.nombre || "";
  };

  const showOcupacionCrear =
    tipoCrear === "Nacional" && movCrear !== "Observacion";

  const focusAfterLogoCrear = useCallback(() => {
    requestAnimationFrame(() => {
      if (showOcupacionCrear && ocupacionCrearSelectRef.current) {
        ocupacionCrearSelectRef.current.focus();
        return;
      }
      comentarioCrearRef.current?.focus();
    });
  }, [showOcupacionCrear]);

  const focusLogoCrear = useCallback(() => {
    requestAnimationFrame(() => {
      const targetFile =
        formOp.dibujo && logoCrearButtonsRef.current[formOp.dibujo]
          ? formOp.dibujo
          : logosCrear[0];
      const targetBtn = targetFile ? logoCrearButtonsRef.current[targetFile] : null;
      if (targetBtn) {
        targetBtn.focus();
        return;
      }
      focusAfterLogoCrear();
    });
  }, [focusAfterLogoCrear, formOp.dibujo, logosCrear]);

  const handleEnterObsContenedorCrear = () => {
    agregarContenedorCrear();
    requestAnimationFrame(() => {
      contenedorNombreCrearRef.current?.focus();
    });
  };

  /* ==== RENDER PANEL ==== */

  const renderOpsPanel = (panel) => {
    const ops = (dataDia.Operacion || []).filter(
      (op) => op.tipoOperacion === panel.tipoOperacion && op.tipo === panel.tipo
    );

    if (!ops.length) return <div className="empty">Sin operaciones</div>;

    return <OperationDapOrder operations={ops}>{(op) => {
      const esComentario =
        op.esComentario ||
        op.soloComentario ||
        (!!op.textoComentario &&
          (!op.Contenedores || op.Contenedores.length === 0) &&
          (!op.camiones || op.camiones.length === 0));

      if (esComentario) {
        const texto = op.textoComentario || op.comentario || op.observacion || "(sin texto)";
        const nombreClienteVista = resolveOperacionNombreCliente(op);

        return (
          <div key={op._id} className="board-item comentario">
            <div className="board-item-header">
              <div className="board-item-title">{nombreClienteVista || "Comentario"}</div>
            </div>
            <div style={{ fontSize: 12, opacity: 0.9, whiteSpace: "pre-wrap" }}>
              {`“${texto}”`}
            </div>
          </div>
        );
      }

      const opNorm = normalizarCamiones(op);
      const cant = Math.max(1, Number(opNorm.cantidadCamiones || 1));
      const nombreClienteVista = resolveOperacionNombreCliente(opNorm);
      const transfers = getOperationTransfers(opNorm, fecha);
      const transferEvents = {
        onMouseEnter: (event) => { event.stopPropagation(); clearHoverTimeout(); setHoverLogo(null); },
        onFocus: () => { clearHoverTimeout(); setHoverLogo(null); },
      };

      const tienePendientes = getOperationDayState(opNorm, fecha, estadoCamionPorLogo).hasPending;
      const ocupacionNombre =
        opNorm.tipoOperacion === "Nacional" ? resolveOcupacionNombre(opNorm) : "";
      const cargasSalida = (() => {
        if (opNorm.tipo !== "Egreso") return [];

        const textos = [
          getOperacionObservacion(opNorm),
          ...(opNorm.Contenedores || []).map((item) => {
            const texto = getTextoObservacionCamion(item);
            if (!texto) return "";
            if (cant <= 1) return texto;
            return `Camión ${Number(item.camionIndex || 0) + 1}: ${texto}`;
          }),
        ]
          .map((texto) => String(texto || "").trim())
          .filter(Boolean);

        const vistos = new Set();
        return textos.filter((texto) => {
          const key = texto.toLocaleLowerCase("es");
          if (vistos.has(key)) return false;
          vistos.add(key);
          return true;
        });
      })();
      const abrirResumenOperacion = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (
          hoverLogo?.tipo === "operacion" &&
          String(hoverLogo.opId || "") === String(opNorm._id || "")
        ) {
          setHoverLogo(null);
          return;
        }
        showHoverOperacion(event, opNorm);
      };

      return (
        <div
          key={op._id}
          className={"board-item" + (tienePendientes ? " pending" : "") +
            (transfers.incoming ? " operation-has-incoming-transfer" : "")}
        >
          <OperationTransferIndicator direction="incoming" label={transfers.incoming}
            className="operation-transfer-indicator" {...transferEvents} />
          <div className="board-item-header">
            <div className={"board-item-main" + (cant > 3 ? " board-item-main--many-trucks" : "")}>
              <div className="board-item-title">
                {nombreClienteVista}
                <OperationTransferIndicator direction="outgoing" label={transfers.outgoing}
                  className="operation-transfer-indicator" {...transferEvents} />
              </div>
              <div className="truck-logos-header" style={{ "--truck-columns": Math.min(4, cant) }}>
                {Array.from({ length: cant }).map((_, idx) => {
                  const camion = (opNorm.camiones || []).find((c) => c.index === idx) || null;
                  const imgName = camion?.dibujo || opNorm.dibujo || "";

                  const estado = estadoCamionPorLogo(imgName);
                  let cls = "truck-logo";
                  if (estado === "pendiente") cls += " pending";
                  else if (estado === "proceso") cls += " inprogress";
                  else if (estado === "finalizado") cls += " finalizado";

                  return (
                    <div
                      key={idx}
                      className={cls}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCamion(opNorm, idx);
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        showHoverCamion(e, opNorm, idx, imgName);
                      }}
                      onMouseLeave={scheduleHideHover}
                      title={`Camión ${idx + 1}`}
                    >
                      <TruckLogoVisual
                        imageName={imgName}
                        dap={opNorm.dap}
                        label={`Camion de ${nombreClienteVista}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              className="board-item-open"
              onClick={abrirResumenOperacion}
              onMouseLeave={scheduleHideHover}
              aria-label={`Ver resumen de ${nombreClienteVista || "la operación"}`}
              title="Ver resumen"
            >
              <FiMenu />
            </button>
          </div>

          {cargasSalida.length > 0 && (
            <div className="board-item-note" title={cargasSalida.join(" · ")}>
              <span className="board-item-note-label">Cargas que salen</span>
              <div className="board-item-note-text">
                {cargasSalida.map((texto, idx) => {
                  const partes = texto.match(/^(Camión\s+\d+:)\s*(.*)$/i);
                  return (
                    <div className="board-item-note-entry" key={`${texto}-${idx}`}>
                      {partes ? (
                        <>
                          <span className="board-item-note-truck">{partes[1]}</span>{" "}
                          <span>{partes[2]}</span>
                        </>
                      ) : (
                        texto
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {ocupacionNombre && (
            <div style={{ fontSize: 11, color: "var(--muted)" }}>
              Sucursal: {ocupacionNombre}
            </div>
          )}
        </div>
      );
    }}</OperationDapOrder>;
  };

  const renderObsPanel = (panel) => {
  const ambitoPanel = (panel.observAmbito || "").toLowerCase();
  const lista = (dataDia.observaciones_anotaciones || []).filter(
    (o) => (o.ambito || "").toLowerCase() === ambitoPanel
  );

  if (!lista.length) return <div className="empty">Sin observaciones</div>;

  return (
    <div className="daily-observations-list">
      {lista.map((o) => {
        const done = !!o.completada;

        return (
          <div key={o._id} className={`obs-row${done ? " done" : ""}`}>
            <label className="obs-row-main">
              <input
                type="checkbox"
                checked={done}
                onChange={() => toggleObs(o)}
                onClick={(e) => e.stopPropagation()}
              />

              <span className="obs-row-text">
                {o.programacionId && <ScheduledActivityMark />}
                {o.observacion || "(sin texto)"}
              </span>
            </label>

            <div className="obs-row-actions">
              <button
                type="button"
                className="obs-action"
                onClick={() => abrirEditarObservacion(o)}
                title="Editar observación"
              >
                <FiEdit2 />
                <span>Editar</span>
              </button>
              <button
                type="button"
                className="obs-action danger"
                onClick={() => eliminarObservacion(o)}
                title="Eliminar observación"
              >
                <FiTrash2 />
                <span>Eliminar</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};


  /* ================== RENDER ================== */

  const areaClassById = (id) => {
    switch (id) {
      case "fi-in":
        return "area-fi-in";
      case "fi-eg":
        return "area-fi-eg";
      case "fe-in":
        return "area-fe-in";
      case "fe-eg":
        return "area-fe-eg";
      case "na-in":
        return "area-na-in";
      case "na-eg":
        return "area-na-eg";
      case "obs-fi":
        return "area-obs-fi";
      case "obs-na":
        return "area-obs-na";
      default:
        return "";
    }
  };

  return (
    <div className="users-wrap daily-operations-page">
      <header className="users-header">
        <div className="title-stack">
          <h1>Operaciones Diarias</h1>
          <small>{depositoLabel}</small>
        </div>
        <div className="toolbar">
          <div className="input-search">
            <FiCalendar />
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value || HOY)} />
          </div>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setClientesReload((value) => value + 1);
              fetchData();
            }}
            disabled={loading || clientesLoading}
            aria-busy={loading || clientesLoading}
          >
            <FiRefreshCw aria-hidden="true" />
            <span>{loading || clientesLoading ? "Cargando..." : "Actualizar"}</span>
          </button>
        </div>
        <div className="toolbar-center">
  <button
    className="btn ghost"
    onClick={() =>
      navigate(
        `/operaciones?fecha=${fecha}&deposito=${depositoActual}`
      )
    }
  >
    <FiCalendar /> Semanal
  </button>

  <button
    className="btn ghost"
    onClick={() =>
      navigate(
        `/operaciones?fecha=${fecha}&deposito=${depositoActual}&cal=1`
      )
    }
  >
    <FiCalendar /> Mensual
  </button>
  <ScheduledActivities deposito={depositoActual} selectedDate={fecha} onChanged={fetchData} />
</div>
      </header>

      {loading && <div className="panel empty">Cargando…</div>}

      <section className="panel panel-diario">
        <div className="daily-grid">
          {PANELS_DIA.map((p) => (
            <div key={p.id} className={`daily-card ${areaClassById(p.id)}`}>
              <div className="daily-card-header">
                <div>
                  <div className="daily-card-title">{p.titulo}</div>
                </div>
                {p.tipoOperacion && (
                  <button
                    className="cell-add"
                    title="Agregar operación"
                    onClick={() => abrirCrearOperacion(p.tipoOperacion, p.tipo)}
                  >
                    <FiPlus />
                  </button>
                )}
                {p.observAmbito && (
                  <button
                    className="cell-add"
                    title="Agregar observación"
                    onClick={() => abrirCrearObservacion(p.observAmbito)}
                  >
                    <FiPlus />
                  </button>
                )}
              </div>
              <div className="daily-card-body">{p.tipoOperacion ? renderOpsPanel(p) : renderObsPanel(p)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* POPUP WIN98 */}
      {hoverLogo && (
        <div
          className="win98-pop"
          style={{
            left: hoverLogo.x,
            top: hoverLogo.y,
            transform: "translate(-50%, 0)",
          }}
          onMouseEnter={clearHoverTimeout}
          onMouseLeave={scheduleHideHover}
        >
          <h4>
            {hoverLogo.tipo === "operacion"
              ? `Operación — ${hoverLogo.cliente}`
              : `Camión ${hoverLogo.camionIndex + 1}`}
          </h4>

          {hoverLogo.tipo === "operacion" && getOperacionObservacion(hoverLogo.op) && (
            <div
              style={{
                fontSize: 12,
                marginBottom: 8,
                padding: "6px 8px",
                background: "#e5e7eb",
                border: "1px solid #9ca3af",
                borderRadius: 4,
                color: "#111827",
                fontStyle: "italic",
                whiteSpace: "pre-wrap",
              }}
            >
              {getOperacionObservacion(hoverLogo.op)}
            </div>
          )}

          <div style={{ fontSize: 12, marginBottom: 6, color: "#111827", opacity: 0.9 }}>
            {hoverLogo.tipo === "operacion" ? hoverLogo.op?.tipoOperacion : hoverLogo.cliente}
          </div>

          {hoverLogo.tipo === "operacion" ? (
            <>
              <div className="win98-list">
                {(hoverLogo.camiones || []).map((c) => (
                  <div key={c.idx} style={{ marginBottom: 8 }}>
                  <div className="win98-subtitle">
                    {`Camión ${c.idx + 1}`}{" "}
                    <span style={{ fontWeight: 700, marginLeft: 6 }}>
                      {c.estado === "finalizado"
                        ? "Finalizado"
                        : c.estado === "proceso"
                        ? "En proceso"
                        : "Pendiente"}
                    </span>
                  </div>

                  {c.contenedores?.length ? (
                    c.contenedores.map((cc) => (
                      <div key={cc.__idx} className="win98-row" style={{ marginBottom: 4 }}>
                        <span className="win98-name">{cc.nombre || "Sin nombre"}</span>
                        <span style={{ fontSize: 12 }}>{cc.estado || "Sin estado"}</span>
                      </div>
                    ))
                  ) : (
                    <div className="win98-empty">Sin observaciones</div>
                  )}
                  {hoverLogo.canToggleCielFinanzas && (
                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        flexWrap: "wrap",
                        marginTop: 6,
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
                      >
                        <input
                          type="checkbox"
                          checked={!!hoverLogo.siel}
                          onChange={(e) =>
                            handleHoverControl("siel", e.target.checked)
                          }
                        />
                        {SIEL_LABEL}
                      </label>
                      <label
                        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
                      >
                        <input
                          type="checkbox"
                          checked={!!hoverLogo.facturacion}
                          onChange={(e) =>
                            handleHoverControl("facturacion", e.target.checked)
                          }
                        />
                        {FACTURACION_LABEL}
                      </label>
                    </div>
                  )}
              </div>
                ))}
              </div>
            </>
          ) : hoverLogo.contenedores?.length ? (
            <div className="win98-list">
              {hoverLogo.contenedores.map((c) => (
                <div key={c.__idx} className="win98-row">
                  <span className="win98-name">{c.nombre || "Sin nombre"}</span>
                  <span style={{ fontSize: 12 }}>{c.estado || "Sin estado"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="win98-empty">Sin observaciones</div>
          )}

          {hoverLogo.tipo !== "operacion" && (
            <>
              <div className="win98-row">
                <label>Estado</label>
                <div style={{ fontSize: 12 }}>
                  {hoverLogo.estado === "finalizado"
                    ? "Finalizado"
                    : hoverLogo.estado === "proceso"
                    ? "En proceso"
                    : "Pendiente"}
                </div>
              </div>
              <div className="win98-row">
                <label>Logo</label>
                <TruckLogoDropdown value={hoverLogo.logo} onChange={handleHoverLogo}
                  logos={logosHover} movement={hoverLogo.op?.tipo} operationType={hoverLogo.op?.tipoOperacion} logoUrl={logoUrl} dap={hoverLogo.op?.dap} />
              </div>
              {hoverLogo.canToggleCielFinanzas && (
                <div className="win98-row" style={{ display: "block" }}>
                  <label style={{ display: "block", marginBottom: 6 }}>
                    {CIEL_SIEL_LABEL}
                  </label>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
                    >
                      <input
                        type="checkbox"
                        checked={!!hoverLogo.siel}
                        onChange={(e) =>
                          handleHoverControl("siel", e.target.checked)
                        }
                      />
                      {SIEL_LABEL}
                    </label>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
                    >
                      <input
                        type="checkbox"
                        checked={!!hoverLogo.facturacion}
                        onChange={(e) =>
                          handleHoverControl("facturacion", e.target.checked)
                        }
                      />
                      {FACTURACION_LABEL}
                    </label>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="win98-actions">
            <button
              type="button"
              className="win98-btn"
              onClick={() => {
                if (hoverLogo.tipo === "operacion" && hoverLogo.op) {
                  abrirDetalleOperacion(hoverLogo.op);
                  return;
                }
                const op = (dataDia.Operacion || []).find((o) => o._id === hoverLogo.opId);
                if (op && hoverLogo.tipo === "camion") openCamion(op, hoverLogo.camionIndex);
              }}
            >
              {hoverLogo.tipo === "operacion" ? "Editar información" : "Editar observaciones"}
            </button>
          </div>
        </div>
      )}

      {/* MODAL OPERACION (CONTENEDORES + OBS) */}
      {modalOp && (
        <div className="drawer-overlay show" onClick={() => setModalOp(null)}>
          <div
            className="drawer open ops-modal ops-modal--wide"
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 120 }}
          >
            <div className="drawer-header">
              <div>
                <h3>Operación — {resolveOperacionNombreCliente(modalOp)}</h3>
                <small style={{ color: "var(--muted)" }}>
                  {modalOp.tipoOperacion} · {modalOp.tipo} · {fecha}
                </small>
              </div>
              <button className="btn icon" onClick={() => setModalOp(null)}>
                <FiX />
              </button>
            </div>

            <div className="drawer-body">
              <div className="field">
                <label>Observación general de la operación</label>
                <textarea
                  rows={3}
                  value={opObsDraft.texto}
                  onChange={(e) =>
                    setOpObsDraft((p) => ({ ...p, texto: e.target.value }))
                  }
                  placeholder="Comentario general de esta operación…"
                />
              </div>

              <hr style={{ opacity: 0.2, margin: "16px 0" }} />

              <div className="field">
                <label>Camiones y observaciones</label>
                <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 8 }}>
                  Abrí un camión para editar sus observaciones o eliminarlo.
                </div>

                <div className="op-camiones-list">
                  {(modalOp.camiones || []).map((cam) => {
                    const lista = (modalOp.Contenedores || []).filter(
                      (c) => Number(c.camionIndex || 0) === Number(cam.index)
                    );

                    const abrirCamion = () => {
                      setModalOp(null);
                      openCamion(modalOp, cam.index, { returnToOperationModal: true });
                    };

                    return (
                      <div
                        key={cam.index}
                        className="op-camion-card clickable"
                        role="button"
                        tabIndex={0}
                        onClick={abrirCamion}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter" && e.key !== " ") return;
                          e.preventDefault();
                          abrirCamion();
                        }}
                      >
                        <div className="op-camion-head">
                          <div className="op-camion-title">
                            <span className="op-camion-dot" />
                            <span>Camión {cam.index + 1}</span>
                          </div>
                          <span className="op-camion-open">Abrir</span>
                        </div>

                        {lista.length === 0 ? (
                          <div className="op-camion-empty">Sin observaciones</div>
                        ) : (
                          lista.map((c, idx) => (
                            <div key={idx} className="op-camion-row">
                              <span className="op-camion-row-name">
                                <b>{c.nombre}</b>
                                {c.obs && <span style={{ opacity: 0.6 }}> ({c.obs})</span>}
                              </span>
                              <span className="op-camion-row-state">
                                {c.estado || "Completar"}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="drawer-footer">
              <div>
                <button className="btn danger" onClick={borrarOperacionDesdeModal}>
                  <FiTrash2 /> Eliminar operación
                </button>
              </div>
              <div>
                <button className="btn" onClick={() => setModalOp(null)}>
                  <FiX /> Cerrar
                </button>

                <button className="btn" onClick={() => agregarNuevoCamion(modalOp)}>
                  Nuevo camión
                </button>

                <button className="btn solid" onClick={guardarObsOperacion}>
                  <FiSave /> Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DRAWER CREAR */}
      <Drawer open={crearOpen} onClose={() => setCrearOpen(false)} className="ops-modal">
        <div className="drawer-header">
          <h3>
            {movCrear === "Observacion"
              ? `Crear observación — ${tipoCrear}`
              : `Crear operación — ${tipoCrear} · ${movCrear}`}
          </h3>
          <button className="btn icon" onClick={() => setCrearOpen(false)}>
            <FiX />
          </button>
        </div>

        <div className="drawer-body">
          {movCrear === "Observacion" ? (
            <>
              <div className="field">
                <label>Observación</label>
                <input
                  value={formObs.observacion}
                  onChange={(e) =>
                    setFormObs((prev) => ({ ...prev, observacion: e.target.value }))
                  }
                  placeholder="Notas del día…"
                />
              </div>

              <div className="field">
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={!!formObs.completada}
                    onChange={(e) =>
                      setFormObs((prev) => ({ ...prev, completada: e.target.checked }))
                    }
                  />
                  Marcar como completada
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="form-grid operation-identity-grid">
                <div className="operation-client-fields">
                  <SelectCliente
                    clientes={clientesReferencia} loading={clientesLoading} error={clientesError}
                    onRetry={() => setClientesReload((value) => value + 1)}
                    value={formOp.nombreCliente}
                    onChange={(v) => setFormOp((f) => ({ ...f, nombreCliente: v }))}
                    onSelect={(cli) =>
                      setFormOp((f) => ({
                        ...f,
                        nombreCliente: getClienteDisplayName(cli),
                        clienteId: String(cli?._id || ""),
                        clienteCodigo: String(cli?.codCliente || ""),
                        clienteNombre: getClienteDisplayName(cli),
                        cliente: cli || null,
                      }))
                    }
                    inputRef={clienteCrearInputRef}
                    onEnterNext={focusLogoCrear}
                    highlightOnEntry={crearOpen && movCrear !== "Observacion" && !formOp.cliente}
                  />
                  <label className="truck-dap-option">
                    <input
                      type="checkbox"
                      checked={formOp.dap}
                      onChange={(e) => {
                        const dap = e.target.checked;
                        setFormOp((f) => ({ ...f, dap,
                          dibujo: isTruckLogoAllowed(f.dibujo, movCrear, { dap, operationType: tipoCrear }) ? f.dibujo : "",
                        }));
                      }}
                    />
                    D.A.P
                  </label>
                  <FiscalOutChecks operationType={tipoCrear} movement={movCrear} value={formOp}
                    onChange={(field, checked) => setFormOp((form) => ({ ...form, [field]: checked }))} />
                </div>

                <LogoSelect
                  value={formOp.dibujo}
                  onChange={(file) => setFormOp((f) => ({ ...f, dibujo: file }))}
                  logos={logosCrear}
                  dap={formOp.dap}
                  getButtonRef={setLogoCrearButtonRef}
                  onEnterNext={focusAfterLogoCrear}
                />
              </div>

              {showOcupacionCrear && (
                <div className="field">
                  <label>Factor ocupación (sucursal)</label>
                  <select
                    ref={ocupacionCrearSelectRef}
                    value={formOp.ocupacionId || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const selected = (ocupaciones || []).find(
                        (o) => String(o?._id) === String(id)
                      );
                      setFormOp((f) => ({
                        ...f,
                        ocupacionId: id,
                        ocupacionNombre: selected?.nombre || "",
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      comentarioCrearRef.current?.focus();
                    }}
                    disabled={ocuLoading}
                  >
                    <option value="">{ocuLoading ? "Cargando..." : "Sin asignar"}</option>
                    {(ocupaciones || []).map((o) => (
                      <option key={o._id} value={o._id}>
                        {o.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="field">
                <label>Observaciones de la operativa</label>
                <textarea
                  ref={comentarioCrearRef}
                  rows={2}
                  value={formOp.comentarioGeneral || ""}
                  onChange={(e) =>
                    setFormOp((f) => ({ ...f, comentarioGeneral: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" || e.shiftKey) return;
                    e.preventDefault();
                    cantidadCamionesCrearRef.current?.focus();
                  }}
                  placeholder="Comentario para esta operación…"
                />
              </div>

              <div className="truck-creation-details">
                <div className="field truck-count-field">
                  <label htmlFor="daily-create-truck-count">Cantidad de camiones</label>
                  <input
                    ref={cantidadCamionesCrearRef}
                    id="daily-create-truck-count"
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={formOp.cantidadCamiones}
                    onChange={(e) =>
                      setFormOp((f) => ({
                        ...f,
                        cantidadCamiones: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      contenedorNombreCrearRef.current?.focus();
                    }}
                  />
                </div>
                <div className="field">
                  <label htmlFor="daily-create-truck-observation">Agregar observaciones al camión</label>
                  <div className="truck-obs-add">
                    <input
                      ref={contenedorNombreCrearRef}
                      id="daily-create-truck-observation"
                      placeholder="Observación"
                      value={contenedorCrear.nombre}
                      onChange={(e) =>
                        setContenedorCrear((c) => ({
                          ...c,
                          nombre: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        handleEnterObsContenedorCrear();
                      }}
                    />
                    <button
                      className="btn"
                      type="button"
                      onClick={handleEnterObsContenedorCrear}
                    >
                      <FiPlus /> Agregar
                    </button>
                  </div>
                </div>
              </div>

              {formOp.Contenedores?.length > 0 && (
                <div className="field">
                  <label>Observaciones del camión</label>
                  <div className="panel">
                    {formOp.Contenedores.map((c, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "6px 0",
                          borderBottom:
                            idx === formOp.Contenedores.length - 1
                              ? "none"
                              : "1px solid rgba(148,163,184,.25)",
                        }}
                      >
                        <b>{c.nombre || c.obs || "Sin observación"}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="drawer-footer">
          <button className="btn" onClick={() => setCrearOpen(false)}>
            <FiX /> Cancelar
          </button>
          <button className="btn solid" onClick={crear}>
            <FiSave /> Guardar
          </button>
        </div>
      </Drawer>

      {editObs && (
        <Drawer open={!!editObs} onClose={cerrarEditarObservacion} className="ops-modal">
          <div className="drawer-header">
            <div>
              <h3>Editar observación — {normalizarAmbitoObs(editObs.ambito) || "Sin ámbito"}</h3>
              <small style={{ color: "var(--muted)" }}>Fecha: {fecha}</small>
            </div>
            <button className="btn icon" onClick={cerrarEditarObservacion}>
              <FiX />
            </button>
          </div>

          <div className="drawer-body">
            {editObs.programacionId && <p className="muted" style={{ fontSize: 12 }}>
              Esta observación pertenece a una actividad programada. Los cambios afectan solamente a esta fecha.
              Para cancelar las repeticiones pendientes, usá «Programar actividad».
            </p>}
            <div className="field">
              <label>Observación</label>
              <input
                ref={editObsInputRef}
                value={editObs.observacion || ""}
                onChange={(e) =>
                  setEditObs((prev) => ({ ...prev, observacion: e.target.value }))
                }
                placeholder="Notas del día…"
              />
            </div>

            <div className="field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!editObs.completada}
                  onChange={(e) =>
                    setEditObs((prev) => ({ ...prev, completada: e.target.checked }))
                  }
                />
                Marcar como completada
              </label>
            </div>
          </div>

          <div className="drawer-footer">
            <div>
              <button className="btn danger" onClick={() => eliminarObservacion(editObs)}>
                <FiTrash2 /> Eliminar
              </button>
            </div>
            <div>
              <button className="btn" onClick={cerrarEditarObservacion}>
                <FiX /> Cancelar
              </button>
              <button className="btn solid" onClick={guardarObservacionEditada}>
                <FiSave /> Guardar
              </button>
            </div>
          </div>
        </Drawer>
      )}

      {/* DRAWER EDICIÓN CAMIÓN */}
      {editOp && (
        <Drawer
          open={!!editOp}
          onClose={closeCamionEditor}
          className="ops-modal ops-modal--wide"
        >
          <div className="drawer-header">
            <div>
              <h3>
                Observaciones — {resolveOperacionNombreCliente(editOp)} · Camión {camionActivo + 1}
              </h3>
              <small style={{ color: "var(--muted)" }}>
                {editOp.tipoOperacion} · {editOp.tipo} · {fecha}
              </small>
            </div>
            <button className="btn icon" onClick={closeCamionEditor}>
              <FiX />
            </button>
          </div>

          <div className="drawer-body">
            <div className="form-grid operation-identity-grid">
              <SelectCliente
                clientes={clientesReferencia} loading={clientesLoading} error={clientesError}
                onRetry={() => setClientesReload((value) => value + 1)}
                value={resolveOperacionNombreCliente(editOp)}
                onChange={(v) => setEditOp((op) => ({ ...op, nombreCliente: v }))}
                onSelect={(cli) =>
                  setEditOp((op) => ({
                    ...op,
                    nombreCliente: getClienteDisplayName(cli),
                    clienteId: String(cli?._id || ""),
                    clienteCodigo: String(cli?.codCliente || ""),
                    clienteNombre: getClienteDisplayName(cli),
                    cliente: cli || null,
                  }))
                }
              />

              <LogoSelect
                value={
                  (editOp.camiones || []).find((c) => c.index === camionActivo)?.dibujo ||
                  editOp.dibujo ||
                  ""
                }
                onChange={(file) =>
                  setEditOp((op) => {
                    const camiones = [...(op.camiones || [])];
                    const idx = camiones.findIndex((c) => c.index === camionActivo);
                    if (idx >= 0) camiones[idx] = { ...camiones[idx], dibujo: file };
                    else camiones.push({ index: camionActivo, dibujo: file });

                    // ? auto-close en UI también (para que cuando guardes ya esté todo final)
                    const opNorm = normalizarCamiones({ ...op, camiones });
                    const opFinal = aplicarAutoCloseEnOp(opNorm, camionActivo, file);
                    return { ...opFinal };
                  })
                }
                logos={logosEdicion}
                dap={editOp.dap}
              />
            </div>

            <div className="field">
              <label>Observación general de la operación</label>
              <textarea
                rows={3}
                value={editOp.comentarioGeneral || ""}
                onChange={(e) =>
                  setEditOp((op) => ({
                    ...op,
                    comentarioGeneral: e.target.value,
                  }))
                }
                placeholder="Comentario general de esta operación…"
              />
            </div>

            {editOp.tipoOperacion === "Nacional" &&
              !editOp.esComentario &&
              !editOp.soloComentario && (
                <div className="field">
                  <label>Factor ocupación (sucursal)</label>
                  <select
                    value={resolveOcupacionId(editOp)}
                    onChange={(e) => {
                      const id = e.target.value;
                      const selected = (ocupaciones || []).find(
                        (o) => String(o?._id) === String(id)
                      );
                      setEditOp((op) => ({
                        ...op,
                        ocupacionId: id,
                        ocupacionNombre: selected?.nombre || "",
                      }));
                    }}
                    disabled={ocuLoading}
                  >
                    <option value="">{ocuLoading ? "Cargando..." : "Sin asignar"}</option>
                    {(ocupaciones || []).map((o) => (
                      <option key={o._id} value={o._id}>
                        {o.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div className="field">
              <label>Agregar observaciones al camión</label>
              <div className="truck-obs-add">
                <input
                  placeholder="Observación"
                  value={contenedorDraft.nombre}
                  onChange={(e) =>
                    setContenedorDraft((c) => ({
                      ...c,
                      nombre: e.target.value,
                    }))
                  }
                />
                <button className="btn" type="button" onClick={agregarContenedorCamion}>
                  <FiPlus /> Agregar
                </button>
              </div>
            </div>

            <div className="field">
              <label>Observaciones del camión {camionActivo + 1}</label>
              <div className="panel">
                {(editOp.Contenedores || []).filter((c) => (c.camionIndex || 0) === camionActivo)
                  .length === 0 && <div className="empty">No hay observaciones para este camión.</div>}

                {(editOp.Contenedores || []).map((c, idx) => {
                  if ((c.camionIndex || 0) !== camionActivo) return null;
                  const estaEditando = contenedorEditando.index === idx;
                  return (
                    <div key={idx} className="row-line truck-obs-row">
                      <div className="truck-obs-main">
                        {estaEditando ? (
                          <input
                            placeholder="Observación"
                            value={contenedorEditando.nombre}
                            onChange={(e) =>
                              setContenedorEditando((prev) => ({
                                ...prev,
                                nombre: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          <b>{getTextoObservacionCamion(c) || "Sin observación"}</b>
                        )}
                      </div>
                      <div className="truck-obs-controls">
                        <select
                          className="truck-obs-state"
                          value={c.estado || "Completar"}
                          onChange={(e) => cambiarEstadoContenedor(idx, e.target.value)}
                        >
                          <option value="Completar">Completar</option>
                          <option value="Finalizado">Finalizado</option>
                        </select>
                        <button
                          className="btn icon solid"
                          type="button"
                          onClick={() =>
                            estaEditando
                              ? guardarEdicionContenedor(idx)
                              : editarContenedor(idx)
                          }
                          aria-label={
                            estaEditando ? "Guardar observación" : "Editar observación"
                          }
                          title={estaEditando ? "Guardar observación" : "Editar observación"}
                        >
                          {estaEditando ? <FiSave /> : <FiEdit2 />}
                        </button>
                        <button
                          className="btn icon"
                          type="button"
                          onClick={() => eliminarContenedor(idx)}
                          title="Eliminar observación"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {puedeEditarCielFinanzas && (
              <div className="field">
                <label>{CIEL_SIEL_LABEL}</label>
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      disabled={!puedeEditarCielFinanzas}
                      checked={sielActivo}
                      onChange={(e) => setOperacionControl("siel", e.target.checked)}
                    />
                    {SIEL_LABEL}
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      disabled={!puedeEditarCielFinanzas}
                      checked={!!facturacionActiva}
                      onChange={(e) =>
                        setOperacionControl(
                          "facturacion",
                          e.target.checked
                        )
                      }
                    />
                    {FACTURACION_LABEL}
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="drawer-footer">
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={borrarCamionIndividual}>
                <FiTrash2 /> Eliminar camión
              </button>
              <button
                className="btn"
                type="button"
                disabled={posponiendo}
                onClick={() => posponerOperacion()}
              >
                {posponiendo ? "Pasando…" : "Pasar a mañana"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {camionReturnTarget?.type === "operation-modal" && (
                <button className="btn" type="button" onClick={volverAListadoOperacion}>
                  <FiChevronLeft /> Volver
                </button>
              )}
              <button className="btn" type="button" onClick={cerrarTodosContenedoresCamion}>
                Cerrar camión
              </button>
              <button className="btn" onClick={closeCamionEditor}>
                <FiX /> Cancelar
              </button>
              <button className="btn solid" onClick={guardarCamion}>
                <FiSave /> Guardar
              </button>
            </div>
          </div>
        </Drawer>
      )}

    </div>
  );
}








